import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { useToast } from '@/components/ui/use-toast';
import { useCurrency } from '@/contexts/CurrencyContext';
import { Radio, Mic, Users, Clock, Play, Pause, Square, QrCode, ExternalLink, Settings, Volume2, VolumeX, MoreHorizontal, ArrowUp, ArrowDown, UserX, X, ArrowRight, Music, ChevronDown, Check, Tv, Maximize2, ScrollText, HelpCircle, Star, History, RotateCcw } from 'lucide-react';
import QRCode from 'qrcode.react';
import { buildKaraokePublicUrl, buildKaraokeTvUrl, buildKaraokeTvControlUrl } from '@/lib/urlHelpers';
import { db } from '@/lib/firebase';
import {
  doc,
  setDoc,
  deleteDoc,
  getDoc,
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  writeBatch,
  getDocs,
  addDoc,
  enableNetwork,
} from 'firebase/firestore';
import { storeSessionHistory } from '@/lib/sessionHistory';
import { buildKaraokeSessionHistoryExtras } from '@/lib/karaokeSessionAnalytics';
import {
  mergeRequestSnapshotsById,
  getKaraokeRequestsForSessionWindow,
  getLegacyGatewayKaraokeRowsFromRequests,
} from '@/lib/sessionFunnelQueries';
import { isPaidSongRequestForLedger } from '@/lib/payoutLedger';
import { capitalizeWords } from '@/lib/utils';
import { useMobile } from '@/features/shared/contexts/MobileContext';
import MobileKaraokeLiveDashboard from '@/features/karaoke-mode/components/MobileKaraokeLiveDashboard';
import KaraokeLDTour from './KaraokeLDTour';

/**
 * Firestore timestamps are Timestamp objects, not date strings — passing one
 * straight to new Date() yields Invalid Date and silently drops the row from
 * every session comparison. Returns null when there is no usable date.
 */
const toDateOrNull = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return isNaN(d.getTime()) ? null : d;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  if (typeof value.seconds === 'number') {
    const d = new Date(value.seconds * 1000);
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const KaraokeLiveDashboardPage = () => {
  const mobile = useMobile();
  const { user, profile, authLoading } = useAuth();
  const { toast } = useToast();
  const { currency, format, symbol } = useCurrency();
  
  const [isLive, setIsLive] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [queue, setQueue] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [historyRequests, setHistoryRequests] = useState([]); // Approved, denied, and performed requests

  const [sessionStats, setSessionStats] = useState({
    totalRequests: 0,      // approved + denied requests
    songsPerformed: 0,     // marked as done
    totalEarnings: 0,      // will be 0 until skip queue payments implemented
    sessionTime: 0         // current session duration in minutes
  });

  // Paid Request Settings
  const [paidRequestSettings, setPaidRequestSettings] = useState({
    enabled: true,
    skipQueuePrice: 4.00,
    maxPaidRequests: 3,
    showWaitTimes: false, // Auto-enabled when paid requests are enabled
    paidRequestsOnly: false // Only accept paid requests (doesn't affect existing free ones)
  });

  // Dynamic Pricing Settings
  const [dynamicPricingEnabled, setDynamicPricingEnabled] = useState(false);
  const [dynamicPricingGroups, setDynamicPricingGroups] = useState([
    { from: 3, to: 8, price: 3.00 }, // Default example (greyed out)
    { from: null, to: null, price: null } // Blank group for adding
  ]);
  const [sessionTimer, setSessionTimer] = useState(120); // Default 2 hours in minutes
  const [showTimerModal, setShowTimerModal] = useState(false);
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [paidRequestSettingsExpanded, setPaidRequestSettingsExpanded] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const [newPosition, setNewPosition] = useState('');
  
  // Session countdown timer
  const [remainingTime, setRemainingTime] = useState(null);
  const [sessionEndTime, setSessionEndTime] = useState(null);
  
  // Song Request Form Editor
  const [showFormEditor, setShowFormEditor] = useState(false);
  const [formFields, setFormFields] = useState([
    { id: 'song_title', label: 'Song Title', type: 'text', required: true, order: 1 },
    { id: 'artist_name', label: 'Original Artist', type: 'text', required: true, order: 2 },
    { id: 'requester_name', label: 'Your Name', type: 'text', required: true, order: 3 },
    { id: 'note', label: 'Note (Optional)', type: 'textarea', required: false, order: 4 },
    { id: 'skip_queue', label: 'Skip the queue (additional fee may apply)', type: 'checkbox', required: false, order: 5 }
  ]);
  const [editingField, setEditingField] = useState(null);
  const prevIsLiveRef = useRef(isLive);

  const getRequestTimeValue = (request) => {
    const approved = toDateOrNull(request?.approved_at);
    if (approved) return approved;
    const created = toDateOrNull(request?.created_at);
    if (created) return created;
    return new Date();
  };
  
  // Up Next Announcement
  const [upNextAnnouncement, setUpNextAnnouncement] = useState(null);
  
  // Undo countdown for ending session
  const [undoVisible, setUndoVisible] = useState(false);
  const [undoTimer, setUndoTimer] = useState(5);
  const [undoIntervalId, setUndoIntervalId] = useState(null);
  
  // Remove confirmation state
  const [removeConfirmationId, setRemoveConfirmationId] = useState(null);
  const [dropdownOpenStates, setDropdownOpenStates] = useState({});
  /** Tracks last `is_live` from live_status snapshot (avoid clearing queues on unrelated field updates). */
  const prevKaraokeLiveStatusRef = useRef(false);
  const lastKaraokeSessionIdRef = useRef(null);

  // TV Display Ticker
  const [showTickerMenu, setShowTickerMenu] = useState(false);
  const [tickerEnabled, setTickerEnabled] = useState(false);
  const [tickerText, setTickerText] = useState('');
  
  // List Is Full Settings
  const [listIsFull, setListIsFull] = useState(false);
  const [checkBackMinutes, setCheckBackMinutes] = useState(15);
  
  // Manual Entry
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualEntryForm, setManualEntryForm] = useState({
    song_title: '',
    artist_name: '',
    requester_name: '',
    note: ''
  });
  
  // Quick Tour
  const [showTour, setShowTour] = useState(false);
  const [hasSeenKldTour, setHasSeenKldTour] = useState(false);
  
  // Test requests state
  const [isCreatingTestRequests, setIsCreatingTestRequests] = useState(false);

  // Function to create test requests
  const createTestRequests = async () => {
    if (!user?.uid || !isLive) {
      toast({
        title: "Cannot Create Test Requests",
        description: "You must be live to create test requests.",
        variant: "destructive"
      });
      return;
    }

    setIsCreatingTestRequests(true);

    const testRequests = [
      {
        song_title: "My Way",
        artist_name: "Frank Sinatra",
        requester_name: "Max Fox"
      },
      {
        song_title: "Story of my Life",
        artist_name: "One Direction",
        requester_name: "Jamie Gilder"
      },
      {
        song_title: "Bohemian Rhapsody",
        artist_name: "Queen",
        requester_name: "Steve Jobs"
      },
      {
        song_title: "Espresso",
        artist_name: "Sabrina Carpenter",
        requester_name: "Cerys Smith"
      }
    ];

    try {
      for (let i = 0; i < testRequests.length; i++) {
        const request = testRequests[i];
        const requestData = {
          karaoke_host_id: user.uid,
          song_title: request.song_title,
          artist_name: request.artist_name,
          requester_name: request.requester_name,
          status: 'pending',
          created_at: new Date().toISOString()
        };

        await addDoc(collection(db, 'karaoke_requests'), requestData);
        console.log(`✅ Test request ${i + 1} created: ${request.song_title}`);

        // Wait 4 seconds before creating the next request (except for the last one)
        if (i < testRequests.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 4000));
        }
      }

      toast({
        title: "Test Requests Created",
        description: `Successfully created ${testRequests.length} test requests in pending status.`,
        variant: "default"
      });
    } catch (error) {
      console.error('❌ Error creating test requests:', error);
      toast({
        title: "Error",
        description: "Failed to create test requests. Please try again.",
        variant: "destructive"
      });
    }

    setIsCreatingTestRequests(false);
  };

  // Check if user has seen KLD tour before
  useEffect(() => {
    if (user?.uid) {
      const seen = localStorage.getItem(`karaoke-kld-tour-seen-${user.uid}`);
      if (seen === 'true') {
        setHasSeenKldTour(true);
      }
    }
  }, [user?.uid]);



  // Tour handlers
  const handleTourComplete = () => {
    setShowTour(false);
    setHasSeenKldTour(true);
    if (user?.uid) {
      localStorage.setItem(`karaoke-kld-tour-seen-${user.uid}`, 'true');
    }
    toast({
      title: "Tour Complete!",
      description: "You're ready to run your karaoke session!",
      variant: "default"
    });
  };

  const handleTourSkip = () => {
    setShowTour(false);
    setHasSeenKldTour(true);
    if (user?.uid) {
      localStorage.setItem(`karaoke-kld-tour-seen-${user.uid}`, 'true');
    }
    toast({
      title: "Tour Skipped",
      description: "You can always access the tour from the dashboard anytime.",
      variant: "default"
    });
  };



  // Calculate session statistics
  const calculateSessionStats = useCallback(() => {
    if (!isLive || !currentSession?.startTime) return;

    // Calculate session time in minutes
    const sessionTime = Math.floor((new Date() - currentSession.startTime) / 1000 / 60);
    
    // For now, earnings are 0 until we implement skip queue payments
    const totalEarnings = 0;

    setSessionStats(prev => ({
      ...prev,
      totalEarnings,
      sessionTime
    }));
  }, [isLive, currentSession?.startTime]);

  // Real-time live_status: keeps mobile + laptop in sync (session, timer, pricing) without refresh
  useEffect(() => {
    if (!user?.uid) return;
    const liveRef = doc(db, 'live_status', user.uid);
    const unsub = onSnapshot(
      liveRef,
      (snap) => {
        const wasLive = prevKaraokeLiveStatusRef.current;

        if (!snap.exists()) {
          if (wasLive) {
            setQueue([]);
            setPendingRequests([]);
            setHistoryRequests([]);
          }
          prevKaraokeLiveStatusRef.current = false;
          lastKaraokeSessionIdRef.current = null;
          setIsLive(false);
          setCurrentSession(null);
          return;
        }

        const data = snap.data();
        const live = !!(data.is_live && data.session_id);

        if (!live) {
          if (wasLive) {
            setQueue([]);
            setPendingRequests([]);
            setHistoryRequests([]);
          }
          prevKaraokeLiveStatusRef.current = false;
          lastKaraokeSessionIdRef.current = null;
          setIsLive(false);
          setCurrentSession(null);
          return;
        }

        const sid = data.session_id;
        if (lastKaraokeSessionIdRef.current && lastKaraokeSessionIdRef.current !== sid) {
          setQueue([]);
          setPendingRequests([]);
          setHistoryRequests([]);
        }
        lastKaraokeSessionIdRef.current = sid;
        prevKaraokeLiveStatusRef.current = true;

        setIsLive(true);
        const st = data.start_time;
        const startTime = st?.toDate ? st.toDate() : st ? new Date(st) : new Date();
        setCurrentSession({
          id: sid,
          startTime,
          status: 'active',
          urlSlug: data.url_slug,
        });

        if (data.session_timer) {
          setSessionTimer(data.session_timer);
        }

        if (data.paid_request_settings) {
          const settings = data.paid_request_settings;
          setPaidRequestSettings({
            enabled: settings.enabled !== undefined ? settings.enabled : true,
            skipQueuePrice: settings.skipQueuePrice !== undefined ? settings.skipQueuePrice : 4.0,
            maxPaidRequests: settings.maxPaidRequests || 3,
            showWaitTimes:
              settings.showWaitTimes !== undefined
                ? settings.showWaitTimes
                : settings.enabled !== undefined
                  ? settings.enabled
                  : true,
            paidRequestsOnly: settings.paidRequestsOnly || false,
          });
        }

        setListIsFull(data.list_is_full || false);
        setCheckBackMinutes(data.check_back_minutes || 15);

        if (data.dynamic_pricing) {
          setDynamicPricingEnabled(data.dynamic_pricing.enabled || false);
          if (data.dynamic_pricing.groups && data.dynamic_pricing.groups.length > 0) {
            setDynamicPricingGroups([
              { from: 3, to: 8, price: 3.0 },
              ...data.dynamic_pricing.groups,
              { from: null, to: null, price: null },
            ]);
          } else {
            setDynamicPricingGroups([
              { from: 3, to: 8, price: 3.0 },
              { from: null, to: null, price: null },
            ]);
          }
        } else {
          setDynamicPricingGroups([
            { from: 3, to: 8, price: 3.0 },
            { from: null, to: null, price: null },
          ]);
        }
      },
      (error) => {
        console.error('live_status listener error:', error);
      }
    );
    return () => {
      prevKaraokeLiveStatusRef.current = false;
      lastKaraokeSessionIdRef.current = null;
      unsub();
    };
  }, [user?.uid]);

  // Resume Firestore when returning to the tab (Chrome Android can stall until refresh)
  useEffect(() => {
    if (!db) return;
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        enableNetwork(db).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Load form fields and ticker settings on component mount
  useEffect(() => {
    if (user?.uid) {
      loadFormFields();
      loadTickerSettings();
    }
  }, [user?.uid]);

  // Reload form fields when paid request settings change (to show/hide skip_queue field)
  useEffect(() => {
    if (user?.uid && paidRequestSettings !== null) {
      loadFormFields();
    }
  }, [paidRequestSettings?.enabled, user?.uid]);

  // Close ticker menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTickerMenu && !event.target.closest('.ticker-menu-container')) {
        setShowTickerMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTickerMenu]);

  // Update session stats when queue or pending requests change
  useEffect(() => {
    calculateSessionStats();
  }, [calculateSessionStats]);

  // Reset session stats only when transitioning from live to offline
  useEffect(() => {
    if (prevIsLiveRef.current && !isLive) {
      setSessionStats({
        totalRequests: 0,
        songsPerformed: 0,
        totalEarnings: 0,
        sessionTime: 0
      });
      console.log('📊 Session stats reset - session ended');
    }
    prevIsLiveRef.current = isLive;
  }, [isLive]);

  // Session countdown timer effect
  useEffect(() => {
    if (!isLive || !currentSession?.startTime || !sessionTimer) return;

    // Calculate session end time
    const startTime = currentSession.startTime;
    const endTime = new Date(startTime.getTime() + (sessionTimer * 60 * 1000));
    setSessionEndTime(endTime);

    // Update countdown every second
    const interval = setInterval(() => {
      const now = new Date();
      const timeLeft = Math.max(0, endTime.getTime() - now.getTime());
      
      if (timeLeft <= 0) {
        // Session has ended - automatically stop session to save resources
        setRemainingTime(0);
        clearInterval(interval);
        console.log('⏰ Session timer reached 0 - automatically stopping session to save resources');
        
        toast({
          title: "Session Ended",
          description: "Your karaoke session has ended automatically to save resources.",
          variant: "default"
        });
        
        // Automatically stop the session to clean up all connections
        // This will trigger the useEffect cleanup and stop all listeners
        stopSession();
      } else {
        setRemainingTime(timeLeft);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isLive, currentSession?.startTime, sessionTimer]);

  // Listen for requests when session is live
  useEffect(() => {
    if (!isLive || !user?.uid) return;

    console.log('🎤 Setting up request listeners for karaoke session');

    // Listen for pending requests
    const pendingUnsubscribe = onSnapshot(
      query(
        collection(db, 'karaoke_requests'),
        where('karaoke_host_id', '==', user.uid),
        where('status', '==', 'pending')
      ),
      (snapshot) => {
        const requests = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('🎤 Pending requests updated:', requests.length, requests);
        // Debug: Check requester_name in pending requests
        requests.forEach((request, index) => {
          console.log(`🎤 Pending request ${index}:`, {
            song_title: request.song_title,
            artist_name: request.artist_name,
            requester_name: request.requester_name,
            name: request.name,
            requesterName: request.requesterName,
            note: request.note,
            comment: request.comment,
            notes: request.notes,
            comments: request.comments,
            has_requester: !!request.requester_name,
            has_name: !!request.name,
            has_requesterName: !!request.requesterName,
            has_note: !!(request.note || request.comment || request.notes || request.comments),
            all_fields: Object.keys(request),
            full_object: request
          });
        });
        setPendingRequests(requests);
      },
      (error) => {
        console.error('❌ Error listening to pending requests:', error);
      }
    );

    // Listen for approved requests (queue)
    const approvedUnsubscribe = onSnapshot(
      query(
        collection(db, 'karaoke_requests'),
        where('karaoke_host_id', '==', user.uid),
        where('status', '==', 'approved')
      ),
      (snapshot) => {
        const requests = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort by paid requests first, then by approved_at timestamp
        const sortedRequests = requests.sort((a, b) => {
          // First, prioritize paid requests (skip_queue = true)
          if (a.skip_queue && !b.skip_queue) return -1;
          if (!a.skip_queue && b.skip_queue) return 1;
          
          // If both have the same paid status, sort by approval time
          const aTime = getRequestTimeValue(a).getTime();
          const bTime = getRequestTimeValue(b).getTime();
          return aTime - bTime; // Oldest first (first approved = first in queue)
        });
        
        console.log('🎤 Approved requests updated:', sortedRequests.length, sortedRequests);
        // Debug: Check requester_name in queue
        sortedRequests.forEach((request, index) => {
          console.log(`🎤 Queue item ${index}:`, {
            song_title: request.song_title,
            artist_name: request.artist_name,
            requester_name: request.requester_name,
            name: request.name,
            requesterName: request.requesterName,
            note: request.note,
            comment: request.comment,
            notes: request.notes,
            comments: request.comments,
            has_requester: !!request.requester_name,
            has_name: !!request.name,
            has_requesterName: !!request.requesterName,
            has_note: !!(request.note || request.comment || request.notes || request.comments),
            all_fields: Object.keys(request),
            full_object: request
          });
        });
        setQueue(sortedRequests);
      },
      (error) => {
        console.error('❌ Error listening to approved requests:', error);
      }
    );

    // Listen for all requests to track total requests
    const allRequestsUnsubscribe = onSnapshot(
      query(
        collection(db, 'karaoke_requests'),
        where('karaoke_host_id', '==', user.uid)
      ),
      (snapshot) => {
        const allRequests = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filter requests that were created during this session
        const sessionStartTime = currentSession?.startTime;
        const sessionRequests = sessionStartTime 
          ? allRequests.filter(request => {
              const requestTime = toDateOrNull(request.created_at);
              return requestTime ? requestTime >= sessionStartTime : false;
            })
          : allRequests;
        
        console.log('🎤 Total session requests:', sessionRequests.length);
        
        // Calculate songs performed (status = 'performed')
        const songsPerformed = sessionRequests.filter(request => request.status === 'performed').length;
        
        // Calculate total earnings from paid requests
        const paidRequests = sessionRequests.filter(request => request.skip_queue);
        const totalEarnings = paidRequests.reduce((sum, request) => {
          const paidAmount = Number(request.amount_paid);
          if (!isNaN(paidAmount) && paidAmount > 0) {
            return sum + paidAmount;
          }
          const fallbackPrice = Number(paidRequestSettings.skipQueuePrice) || 0;
          return sum + fallbackPrice;
        }, 0);
        
        setSessionStats(prev => ({
          ...prev,
          totalRequests: sessionRequests.length,
          songsPerformed: songsPerformed,
          totalEarnings: totalEarnings
        }));
      },
      (error) => {
        console.error('❌ Error listening to all requests:', error);
      }
    );

    // Listen for history requests (denied and performed)
    // We listen to all requests and filter client-side since Firestore doesn't support 'in' with multiple values easily
    const historyUnsubscribe = onSnapshot(
      query(
        collection(db, 'karaoke_requests'),
        where('karaoke_host_id', '==', user.uid)
      ),
      (snapshot) => {
        const allRequests = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Terminal / audit states (not pending or approved queue items)
        const historyOnly = allRequests.filter((request) =>
          ['denied', 'performed', 'removed'].includes(request.status)
        );
        
        // Filter to only show requests from current session
        // Use session_id from live_status if available, otherwise use time-based filtering
        const currentSessionId = currentSession?.id;
        const sessionStartTime = currentSession?.startTime;
        const sessionHistory = (currentSessionId || sessionStartTime)
          ? historyOnly.filter(request => {
              // First try to match by session_id if both exist
              if (currentSessionId && request.session_id) {
                return request.session_id === currentSessionId;
              }
              // Fallback: check if request was created after session start
              if (sessionStartTime) {
                const requestTime = toDateOrNull(request.created_at);
                return requestTime ? requestTime >= sessionStartTime : false;
              }
              return true; // If no session info, include all
            })
          : historyOnly;
        
        // Sort by created_at descending (newest first)
        const sortedHistory = sessionHistory.sort((a, b) => {
          const aTime = toDateOrNull(a.created_at)?.getTime() || 0;
          const bTime = toDateOrNull(b.created_at)?.getTime() || 0;
          return bTime - aTime; // Newest first
        });
        
        console.log('📜 History requests updated:', sortedHistory.length);
        setHistoryRequests(sortedHistory);
      },
      (error) => {
        console.error('❌ Error listening to history requests:', error);
      }
    );

    return () => {
      console.log('🧹 Cleaning up request listeners');
      pendingUnsubscribe();
      approvedUnsubscribe();
      allRequestsUnsubscribe();
      historyUnsubscribe();
    };
      }, [isLive, user?.uid, currentSession?.startTime, currentSession?.id]);

  const startSession = async () => {
    if (!profile?.url_slug) {
      toast({
        title: "Profile Setup Required",
        description: "Please set up your karaoke URL in Profile Management first",
        variant: "destructive"
      });
      return;
    }

    // Show timer modal before starting session
    setShowTimerModal(true);
  };

  const confirmStartSession = async () => {
    const sessionId = `karaoke_${Date.now()}`;
    const newSession = {
      id: sessionId,
      startTime: new Date(),
      status: 'active',
      urlSlug: profile.url_slug
    };

    try {
      // Set live status in Firestore
      await setDoc(doc(db, 'live_status', user.uid), {
        is_live: true,
        session_id: sessionId,
        start_time: new Date().toISOString(),
        karaoke_host_id: user.uid,
        url_slug: profile.url_slug,
        session_timer: sessionTimer, // Store timer in Firestore
        paid_request_settings: paidRequestSettings // Store paid request settings
      });

      setCurrentSession(newSession);
      setIsLive(true);
      setShowTimerModal(false);
      
      toast({
        title: "Karaoke Session Started!",
        description: `Your karaoke session is now live and accepting requests. Session will end in ${formatTimer(sessionTimer)}.`,
        variant: "default"
      });
    } catch (error) {
      console.error('Error starting session:', error);
      toast({
        title: "Error Starting Session",
        description: "Failed to start karaoke session. Please try again.",
        variant: "destructive"
      });
    }
  };

  const startUndoCountdown = () => {
    setUndoVisible(true);
    setUndoTimer(5);
    const interval = setInterval(() => {
      setUndoTimer(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          stopSession();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    setUndoIntervalId(interval);
  };

  const cancelUndoCountdown = () => {
    if (undoIntervalId) clearInterval(undoIntervalId);
    setUndoVisible(false);
    setUndoTimer(5);
  };

  const stopSession = async () => {
    try {
      const now = new Date();
      const resolvedSessionId = currentSession?.id || `karaoke_${Date.now()}`;
      const sessionStart = currentSession?.startTime || new Date();

      let serverSnapshot = [];
      let legacyGatewayRows = [];
      if (user?.uid && currentSession?.id && currentSession?.startTime) {
        try {
          serverSnapshot = await getKaraokeRequestsForSessionWindow(
            user.uid,
            currentSession.id,
            currentSession.startTime,
            now
          );
        } catch (e) {
          console.warn('Karaoke session history: Firestore request snapshot failed:', e);
        }
        try {
          legacyGatewayRows = await getLegacyGatewayKaraokeRowsFromRequests(user.uid, currentSession.id);
        } catch (e) {
          console.warn('Karaoke session history: legacy requests merge failed:', e);
        }
      }

      const mergedRequests = mergeRequestSnapshotsById(
        queue,
        pendingRequests,
        historyRequests,
        serverSnapshot,
        legacyGatewayRows
      );

      const durationMinutes =
        sessionStats.sessionTime > 0
          ? sessionStats.sessionTime
          : Math.max(1, Math.round((now - sessionStart) / (1000 * 60)));

      const totalRequests = mergedRequests.length;
      const playedRequests = mergedRequests.filter(
        (r) => r.status === 'performed' || r.status === 'played'
      ).length;
      const totalEarnings = mergedRequests
        .filter((r) => isPaidSongRequestForLedger(r))
        .reduce((sum, r) => sum + (Number(r.amount_paid) || Number(r.total_paid) || 0), 0);

      const sessionData = {
        userId: user.uid,
        sessionId: resolvedSessionId,
        startTime: sessionStart,
        endTime: now,
        durationMinutes,
        totalRequests,
        playedRequests,
        totalEarnings,
        totalTips: 0,
        requests: mergedRequests,
        tips: [],
        venue: profile?.venue_name || '',
        session_mode: 'karaoke',
      };

      let funnelExtras = {};
      if (currentSession?.id && currentSession?.startTime) {
        try {
          funnelExtras = await buildKaraokeSessionHistoryExtras(user.uid, currentSession.id, currentSession.startTime, now);
        } catch (fErr) {
          console.warn('Karaoke funnel (session end) failed:', fErr);
        }
      }
      
      // Store history
      try {
        await storeSessionHistory({ ...sessionData, ...funnelExtras });
        console.log('✅ Karaoke session history saved successfully');
      } catch (historyErr) {
        console.error('❌ Failed to save karaoke session history:', historyErr);
      }

      // Remove live status from Firestore
      await deleteDoc(doc(db, 'live_status', user.uid));

      // Clear all pending and approved requests for this session
      const batch = writeBatch(db);
      
      // Delete all pending requests
      const pendingQuery = query(
        collection(db, 'karaoke_requests'),
        where('karaoke_host_id', '==', user.uid),
        where('status', '==', 'pending')
      );
      const pendingSnapshot = await getDocs(pendingQuery);
      pendingSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      // Delete all approved requests
      const approvedQuery = query(
        collection(db, 'karaoke_requests'),
        where('karaoke_host_id', '==', user.uid),
        where('status', '==', 'approved')
      );
      const approvedSnapshot = await getDocs(approvedQuery);
      approvedSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      await batch.commit();

      // Clear all local state to free up memory
      setIsLive(false);
      setCurrentSession(null);
      setQueue([]);
      setPendingRequests([]);
      setSessionStats({
        totalRequests: 0,
        songsPerformed: 0,
        totalEarnings: 0,
        sessionTime: 0
      });
      setRemainingTime(null);
      setSessionEndTime(null);
      setRemoveConfirmationId(null);
      
      // Clean up any undo countdown intervals
      if (undoIntervalId) {
        clearInterval(undoIntervalId);
        setUndoIntervalId(null);
      }
      setUndoVisible(false);
      setUndoTimer(5);
      
      console.log('🧹 Session stopped - all resources cleaned up');
      
      toast({
        title: "Session Ended",
        description: "Your karaoke session has been ended and all resources cleaned up",
        variant: "default"
      });
    } catch (error) {
      console.error('Error stopping session:', error);
      toast({
        title: "Error Stopping Session",
        description: "Failed to stop karaoke session. Please try again.",
        variant: "destructive"
      });
    }
  };

  const approveRequest = async (requestId) => {
    try {
      await updateDoc(doc(db, 'karaoke_requests', requestId), {
        status: 'approved',
        approved_at: new Date().toISOString()
      });
      
      toast({
        title: "Request Approved",
        description: "Song added to the queue",
        variant: "default"
      });
    } catch (error) {
      console.error('Error approving request:', error);
      toast({
        title: "Error",
        description: "Failed to approve request",
        variant: "destructive"
      });
    }
  };

  const denyRequest = async (requestId) => {
    try {
      await updateDoc(doc(db, 'karaoke_requests', requestId), {
        status: 'denied',
        denied_at: new Date().toISOString()
      });
      
      toast({
        title: "Request Denied",
        description: "Request has been denied",
        variant: "default"
      });
    } catch (error) {
      console.error('Error denying request:', error);
      toast({
        title: "Error",
        description: "Failed to deny request",
        variant: "destructive"
      });
    }
  };

  const reAddRequest = async (requestId) => {
    try {
      await updateDoc(doc(db, 'karaoke_requests', requestId), {
        status: 'pending',
        re_added_at: new Date().toISOString(),
        approved_at: null
      });
      
      toast({
        title: "Request Re-Added",
        description: "Song request has been moved back to pending",
        variant: "default"
      });
    } catch (error) {
      console.error('Error re-adding request:', error);
      toast({
        title: "Error",
        description: "Failed to re-add request",
        variant: "destructive"
      });
    }
  };

  const markAsPerformed = async (song) => {
    try {
      console.log('🎤 Marking song as performed:', song.song_title);
      
      // Update the song status to 'performed' in Firestore
      const songRef = doc(db, 'karaoke_requests', song.id);
      await updateDoc(songRef, { 
        status: 'performed',
        performed_at: new Date().toISOString()
      });
      
      toast({
        title: "Song Marked as Performed",
        description: `${song.song_title} has been performed`,
        variant: "default"
      });
    } catch (error) {
      console.error('Error marking song as performed:', error);
      toast({
        title: "Error",
        description: "Failed to mark song as performed",
        variant: "destructive"
      });
    }
  };

  const submitManualEntry = async () => {
    // Validate required fields
    if (!manualEntryForm.song_title.trim() || !manualEntryForm.artist_name.trim() || !manualEntryForm.requester_name.trim()) {
      toast({
        title: "Missing Information",
        description: "Please fill in Song Title, Artist, and Singer Name",
        variant: "destructive"
      });
      return;
    }

    if (!currentSession || !user?.uid) {
      toast({
        title: "Session Error",
        description: "No active session found",
        variant: "destructive"
      });
      return;
    }

    try {
      // Create request data - directly approved so it goes to queue
      const requestData = {
        karaoke_host_id: user.uid,
        status: 'approved', // Directly approved, goes straight to queue
        song_title: manualEntryForm.song_title.trim(),
        artist_name: manualEntryForm.artist_name.trim(),
        requester_name: manualEntryForm.requester_name.trim(),
        note: manualEntryForm.note.trim() || '',
        session_id: currentSession.id,
        created_at: new Date().toISOString(),
        approved_at: new Date().toISOString(),
        manual_entry: true // Flag to indicate this was manually entered by DJ
      };

      // Add to Firestore
      await addDoc(collection(db, 'karaoke_requests'), requestData);

      // Reset form and close modal
      setManualEntryForm({
        song_title: '',
        artist_name: '',
        requester_name: '',
        note: ''
      });
      setShowManualEntry(false);

      toast({
        title: "Song Added",
        description: `${manualEntryForm.song_title} by ${manualEntryForm.artist_name} has been added to the queue`,
        variant: "default"
      });
    } catch (error) {
      console.error('Error submitting manual entry:', error);
      toast({
        title: "Error",
        description: "Failed to add song to queue",
        variant: "destructive"
      });
    }
  };

  const moveSongUp = async (songIndex) => {
    if (songIndex > 0) {
      const songToMove = queue[songIndex];
      const songAbove = queue[songIndex - 1];
      
      console.log('🔄 Moving song up:', songToMove.song_title, 'from position', songIndex, 'to', songIndex - 1);

      // Update approved_at timestamps to reflect new order
      const batch = writeBatch(db);
      
      // ✅ FIX: Calculate timestamp between the song above and the song two positions above (if it exists)
      let newTimestamp;
      if (songIndex > 1) {
        // There's a song two positions above, so we can place between them
        const songTwoAbove = queue[songIndex - 2];
        const aboveTime = getRequestTimeValue(songTwoAbove).getTime();
        const currentAboveTime = getRequestTimeValue(songAbove).getTime();
        let middleTime = aboveTime + (currentAboveTime - aboveTime) / 2;
        
        // ✅ Ensure we have enough space between timestamps
        if (!isFinite(middleTime) || middleTime === currentAboveTime || middleTime <= aboveTime) {
          middleTime = currentAboveTime - 1000; // Use 1 second gap
        }
        
        // ✅ Ensure we don't go before the song two positions above
        if (middleTime <= aboveTime) {
          middleTime = aboveTime - 1000; // 1 second before
        }
        
        newTimestamp = new Date(middleTime);
      } else {
        // Moving to first position, place it 1 second before the current first song
        newTimestamp = new Date(getRequestTimeValue(songAbove));
        newTimestamp.setMilliseconds(newTimestamp.getMilliseconds() - 1000);
      }
      
      console.log('🔄 New timestamp for moved song:', newTimestamp.toISOString());
      
      const songRef = doc(db, 'karaoke_requests', songToMove.id);
      batch.update(songRef, { approved_at: newTimestamp.toISOString() });
      
      try {
        await batch.commit();
        console.log('✅ Firestore update committed successfully');
        
        // ✅ FIX: Update local state immediately with proper reordering
        setQueue((prev) => {
          const updated = [...prev];
          const [movedSong] = updated.splice(songIndex, 1);
          const reorderedSong = { ...movedSong, approved_at: newTimestamp.toISOString() };
          updated.splice(songIndex - 1, 0, reorderedSong);
          
          // ✅ Sort by paid status first, then by approved_at
          return updated.sort((a, b) => {
            // First, prioritize paid requests
            if (a.skip_queue && !b.skip_queue) return -1;
            if (!a.skip_queue && b.skip_queue) return 1;
            
            // Then sort by approved_at timestamp
            const aTime = getRequestTimeValue(a).getTime();
            const bTime = getRequestTimeValue(b).getTime();
            return aTime - bTime;
          });
        });

        toast({
          title: "Queue Reordered",
          description: "Song moved up in queue",
          variant: "default"
        });
      } catch (error) {
        console.error('❌ Error updating queue order:', error);
        toast({
          title: "Error",
          description: "Failed to update queue order",
          variant: "destructive"
        });
      }
    }
  };

  const moveSongDown = async (songIndex) => {
    if (songIndex < queue.length - 1) {
      const songToMove = queue[songIndex];
      const songBelow = queue[songIndex + 1];
      
      console.log('🔄 Moving song down:', songToMove.song_title, 'from position', songIndex, 'to', songIndex + 1);

      // Update approved_at timestamps to reflect new order
      const batch = writeBatch(db);
      
      // ✅ FIX: Calculate timestamp between the song below and the song two positions below (if it exists)
      let newTimestamp;
      if (songIndex < queue.length - 2) {
        // There's a song two positions below, so we can place between them
        const songTwoBelow = queue[songIndex + 2];
        const belowTime = getRequestTimeValue(songTwoBelow).getTime();
        const currentBelowTime = getRequestTimeValue(songBelow).getTime();
        let middleTime = currentBelowTime + (belowTime - currentBelowTime) / 2;
        
        // ✅ Ensure we have enough space between timestamps
        if (!isFinite(middleTime) || middleTime === currentBelowTime || middleTime >= belowTime) {
          middleTime = currentBelowTime + 1000; // Use 1 second gap
        }
        
        // ✅ Ensure we don't go after the song two positions below
        if (middleTime >= belowTime) {
          middleTime = belowTime - 1000; // 1 second before
        }
        
        newTimestamp = new Date(middleTime);
      } else {
        // Moving to last position, place it 1 second after the current last song
        newTimestamp = new Date(getRequestTimeValue(songBelow));
        newTimestamp.setMilliseconds(newTimestamp.getMilliseconds() + 1000);
      }
      
      console.log('🔄 New timestamp for moved song:', newTimestamp.toISOString());
      
      const songRef = doc(db, 'karaoke_requests', songToMove.id);
      batch.update(songRef, { approved_at: newTimestamp.toISOString() });
      
      try {
        await batch.commit();
        console.log('✅ Firestore update committed successfully');
        
        // ✅ FIX: Update local state immediately with proper reordering
        setQueue((prev) => {
          const updated = [...prev];
          const [movedSong] = updated.splice(songIndex, 1);
          const reorderedSong = { ...movedSong, approved_at: newTimestamp.toISOString() };
          updated.splice(songIndex + 1, 0, reorderedSong);
          
          // ✅ Sort by paid status first, then by approved_at
          return updated.sort((a, b) => {
            // First, prioritize paid requests
            if (a.skip_queue && !b.skip_queue) return -1;
            if (!a.skip_queue && b.skip_queue) return 1;
            
            // Then sort by approved_at timestamp
            const aTime = getRequestTimeValue(a).getTime();
            const bTime = getRequestTimeValue(b).getTime();
            return aTime - bTime;
          });
        });

        toast({
          title: "Queue Reordered",
          description: "Song moved down in queue",
          variant: "default"
        });
      } catch (error) {
        console.error('❌ Error updating queue order:', error);
        toast({
          title: "Error",
          description: "Failed to update queue order",
          variant: "destructive"
        });
      }
    }
  };

  const removeSongFromQueue = async (song) => {
    // If this is the first click, show confirmation
    if (removeConfirmationId !== song.id) {
      setRemoveConfirmationId(song.id);
      // Keep dropdown open for this song
      setDropdownOpenStates(prev => ({ ...prev, [song.id]: true }));
      // Auto-clear confirmation after 5 seconds
      setTimeout(() => {
        setRemoveConfirmationId(null);
        setDropdownOpenStates(prev => ({ ...prev, [song.id]: false }));
      }, 5000);
      return;
    }
    
    // If this is the confirmation click, actually remove the song
    try {
      // Update the song status to 'removed' in Firestore
      const songRef = doc(db, 'karaoke_requests', song.id);
      await updateDoc(songRef, { 
        status: 'removed',
        removed_at: new Date().toISOString()
      });
      
      setRemoveConfirmationId(null); // Clear confirmation state
      setDropdownOpenStates(prev => ({ ...prev, [song.id]: false })); // Close dropdown
      
      toast({
        title: "Song Removed",
        description: `${song.song_title} has been removed from queue`,
        variant: "default"
      });
    } catch (error) {
      console.error('Error removing song from queue:', error);
      toast({
        title: "Error",
        description: "Failed to remove song from queue",
        variant: "destructive"
      });
    }
  };

  const cancelRemoveConfirmation = () => {
    setRemoveConfirmationId(null);
    // Close all dropdowns when canceling
    setDropdownOpenStates({});
  };

  const announceUpNext = async (song) => {
    try {
      console.log('🎤 Announce Up Next - Song data:', song);
      console.log('🎤 Requester name:', song.requester_name);
      console.log('🎤 Song title:', song.song_title);
      console.log('🎤 Artist name:', song.artist_name);
      console.log('🎤 All song fields:', Object.keys(song));
      console.log('🎤 Song object:', JSON.stringify(song, null, 2));
      
      // Validate required fields before announcing
      if (!song.song_title || !song.artist_name) {
        toast({
          title: "Error",
          description: "Song title and artist name are required for announcement",
          variant: "destructive"
        });
        return;
      }

      // Update live_status to trigger announcement on TV Display
      const liveStatusRef = doc(db, 'live_status', user.uid);
      
      // Check for requester name in multiple possible fields
      const requesterName = song.requester_name || song.name || song.requesterName || 'Anonymous';
      
      console.log('🎤 Requester name resolution:', {
        requester_name: song.requester_name,
        name: song.name,
        requesterName: song.requesterName,
        final: requesterName
      });
      
      const announcementData = {
        song_title: song.song_title || 'Unknown Song',
        artist_name: song.artist_name || 'Unknown Artist',
        requester_name: requesterName,
        announced_at: new Date().toISOString()
      };
      
      console.log('🎤 Announcement data being sent:', announcementData);
      
      await updateDoc(liveStatusRef, {
        up_next_announcement: announcementData
      });
      
      toast({
        title: "Up Next Announced!",
        description: `${song.song_title} is now displayed on the TV`,
        variant: "default"
      });
      
      // Clear announcement after 10 seconds
      setTimeout(async () => {
        try {
          await updateDoc(liveStatusRef, {
            up_next_announcement: null
          });
        } catch (error) {
          console.error('Error clearing announcement:', error);
        }
      }, 10000);
      
    } catch (error) {
      console.error('Error announcing up next:', error);
      toast({
        title: "Error",
        description: "Failed to announce up next",
        variant: "destructive"
      });
    }
  };

  const moveToPosition = async (song, targetPosition) => {
    const position = parseInt(targetPosition) - 1; // Convert to 0-based index
    const maxPosition = queue.length - 1;
    const actualPosition = Math.min(position, maxPosition); // Ensure we don't exceed queue length
    
    // Calculate new timestamp based on target position
    let newTimestamp;
    if (actualPosition === 0) {
      // Moving to first position - use timestamp 1 second before the current first song
      const firstSong = queue[0];
      newTimestamp = new Date(firstSong.approved_at);
      newTimestamp.setMilliseconds(newTimestamp.getMilliseconds() - 1000);
    } else if (actualPosition === queue.length - 1) {
      // Moving to last position - use timestamp 1 second after the current last song
      const lastSong = queue[queue.length - 1];
      newTimestamp = new Date(lastSong.approved_at);
      newTimestamp.setMilliseconds(newTimestamp.getMilliseconds() + 1000);
    } else {
      // Moving to middle position - use timestamp between two songs
      const songBefore = queue[actualPosition - 1];
      const songAfter = queue[actualPosition];
      const beforeTime = new Date(songBefore.approved_at).getTime();
      const afterTime = new Date(songAfter.approved_at).getTime();
      const middleTime = beforeTime + (afterTime - beforeTime) / 2;
      newTimestamp = new Date(middleTime);
    }
    
    // Update the song's approved_at timestamp
    const batch = writeBatch(db);
    const songRef = doc(db, 'karaoke_requests', song.id);
    batch.update(songRef, { approved_at: newTimestamp.toISOString() });
    
    try {
      await batch.commit();
      setShowPositionModal(false);
      setSelectedSong(null);
      setNewPosition('');
      
      toast({
        title: "Song Moved",
        description: `${song.song_title} moved to position ${actualPosition + 1}`,
        variant: "default"
      });
    } catch (error) {
      console.error('❌ Error updating queue order:', error);
      toast({
        title: "Error",
        description: "Failed to update queue order",
        variant: "destructive"
      });
    }
  };

  // Song Request Form Editor Functions
  const addFormField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      label: 'New Field',
      type: 'text',
      required: false,
      order: formFields.length + 1
    };
    setFormFields([...formFields, newField]);
    setEditingField(newField);
  };

  const updateFormField = (fieldId, updates) => {
    setFormFields(prev => prev.map(field => 
      field.id === fieldId ? { ...field, ...updates } : field
    ));
  };

  const removeFormField = (fieldId) => {
    // Don't allow removal of required fields (song_title, artist_name, requester_name)
    const requiredFields = ['song_title', 'artist_name', 'requester_name'];
    if (requiredFields.includes(fieldId)) {
      toast({ 
        title: "Cannot Remove", 
        description: "This field is required and cannot be removed.",
        variant: "destructive"
      });
      return;
    }
    
    setFormFields(prev => prev.filter(field => field.id !== fieldId));
    if (editingField?.id === fieldId) {
      setEditingField(null);
    }
  };

  const moveFieldUp = (fieldId) => {
    setFormFields(prev => {
      const newFields = [...prev];
      const index = newFields.findIndex(field => field.id === fieldId);
      if (index > 0) {
        [newFields[index], newFields[index - 1]] = [newFields[index - 1], newFields[index]];
        // Update order numbers
        newFields.forEach((field, idx) => {
          field.order = idx + 1;
        });
      }
      return newFields;
    });
  };

  const moveFieldDown = (fieldId) => {
    setFormFields(prev => {
      const newFields = [...prev];
      const index = newFields.findIndex(field => field.id === fieldId);
      if (index < newFields.length - 1) {
        [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
        // Update order numbers
        newFields.forEach((field, idx) => {
          field.order = idx + 1;
        });
      }
      return newFields;
    });
  };

  const saveFormFields = async () => {
    if (!user?.uid) {
      toast({ 
        title: "Authentication Error", 
        description: "You must be logged in to save form fields.",
        variant: "destructive"
      });
      return;
    }

    try {
      console.log('💾 Saving form fields for user:', user.uid);
      console.log('📝 Form fields to save:', formFields);
      
      // Filter out skip_queue field before saving - it's managed by paid request settings
      const fieldsToSave = formFields.filter(f => f.id !== 'skip_queue');
      
      // Validate form fields before saving
      if (!Array.isArray(fieldsToSave) || fieldsToSave.length === 0) {
        throw new Error('Form fields must be a non-empty array');
      }

      // Save form fields to Firestore (without skip_queue)
      const formData = {
        fields: fieldsToSave,
        updated_at: new Date().toISOString(),
        user_id: user.uid
      };

      console.log('💾 Saving to Firestore:', formData);
      
      await setDoc(doc(db, 'karaoke_form_fields', user.uid), formData);
      
      console.log('✅ Form fields saved successfully');
      
      toast({ 
        title: "Form Updated", 
        description: "Your song request form has been updated successfully." 
      });
      setShowFormEditor(false);
    } catch (error) {
      console.error('❌ Error saving form fields:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        user: user?.uid,
        formFields: formFields
      });
      
      let errorMessage = "Failed to save form fields. Please try again.";
      
      if (error.code === 'permission-denied') {
        errorMessage = "Permission denied. Please check your authentication.";
      } else if (error.code === 'unavailable') {
        errorMessage = "Network error. Please check your connection.";
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      toast({ 
        title: "Error", 
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  // Default form fields structure
  const getDefaultFormFields = () => [
    { id: 'song_title', label: 'Song Title', type: 'text', required: true, order: 1 },
    { id: 'artist_name', label: 'Original Artist', type: 'text', required: true, order: 2 },
    { id: 'requester_name', label: 'Your Name', type: 'text', required: true, order: 3 },
    { id: 'note', label: 'Note (Optional)', type: 'textarea', required: false, order: 4 },
    { id: 'skip_queue', label: 'Skip the queue (additional fee may apply)', type: 'checkbox', required: false, order: 5 }
  ];

  const resetFormFieldsToDefault = async () => {
    const defaultFields = getDefaultFormFields();
    setFormFields(defaultFields);
    
    toast({
      title: "Form Reset",
      description: "Form fields have been reset to default",
      variant: "default"
    });
  };

  const loadFormFields = async () => {
    if (!user?.uid) return;
    
    try {
      console.log('📂 Loading form fields for user:', user.uid);
      
      const docRef = doc(db, 'karaoke_form_fields', user.uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log('📂 Loaded form fields:', data);
        
        if (data.fields && Array.isArray(data.fields)) {
          // Filter out skip_queue field if paid requests are disabled
          // We'll add it back conditionally when rendering
          let fields = data.fields.filter(f => f.id !== 'skip_queue');
          
          // Only add skip_queue field if paid requests are enabled
          if (paidRequestSettings?.enabled) {
            // Check if skip_queue field exists in saved fields
            const skipQueueField = data.fields.find(f => f.id === 'skip_queue');
            if (skipQueueField) {
              fields.push(skipQueueField);
            } else {
              // Add default skip_queue field
              fields.push({
                id: 'skip_queue',
                label: 'Skip the queue (additional fee may apply)',
                type: 'checkbox',
                required: false,
                order: fields.length + 1
              });
            }
          }
          
          setFormFields(fields);
        } else {
          console.log('📂 No valid fields found, using defaults');
        }
      } else {
        console.log('📂 No saved form fields found, using defaults');
      }
    } catch (error) {
      console.error('❌ Error loading form fields:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        user: user?.uid
      });
      
      // Don't show toast for load errors, just use defaults
    }
  };


  const savePaidRequestSettings = async () => {
    if (!user?.uid || !isLive) {
      toast({ 
        title: "Session Required", 
        description: "You must have an active session to save paid request settings.",
        variant: "destructive"
      });
      return;
    }

    // Validate skip queue price
    if (paidRequestSettings.enabled) {
      const price = paidRequestSettings.skipQueuePrice;
      const minPrice = 3.00;
      if (price === null || price === undefined || price === '' || isNaN(price) || price < minPrice) {
        toast({
          title: "Minimum Price Required",
          description: `The Skip Queue price must be at least ${format(minPrice)}.`,
          variant: "destructive"
        });
        return;
      }
      
      // Round to nearest 0.50 increment (or 0.25 for USD if preferred)
      const increment = currency === 'USD' ? 0.25 : 0.50;
      const roundedPrice = Math.round(price / increment) * increment;
      if (roundedPrice !== price) {
        // Update to rounded value
        setPaidRequestSettings({ ...paidRequestSettings, skipQueuePrice: roundedPrice });
        toast({
          title: "Price Adjusted",
          description: `Price rounded to ${format(roundedPrice)}`,
          variant: "default"
        });
      }
    }

    try {
      console.log('💾 Saving paid request settings for user:', user.uid);
      console.log('💰 Settings to save:', paidRequestSettings);
      
      // Update live status with new paid request settings
      await updateDoc(doc(db, 'live_status', user.uid), {
        paid_request_settings: paidRequestSettings,
        updated_at: new Date().toISOString()
      });
      
      console.log('✅ Paid request settings saved successfully');
      
      toast({
        title: "Settings Updated",
        description: "Paid request settings have been saved successfully",
        variant: "default"
      });
    } catch (error) {
      console.error('❌ Error saving paid request settings:', error);
      toast({
        title: "Save Failed",
        description: "Failed to save paid request settings. Please try again.",
        variant: "destructive"
      });
    }
  };

  const saveTickerSettings = async () => {
    if (!user?.uid) return;
    
    try {
      console.log('📺 Saving ticker settings for user:', user.uid);
      
      const tickerData = {
        enabled: tickerEnabled,
        text: tickerText.trim(),
        updated_at: new Date().toISOString()
      };
      
      await setDoc(doc(db, 'karaoke_ticker_settings', user.uid), tickerData);
      
      toast({
        title: "Ticker Settings Saved",
        description: tickerEnabled ? "Ticker is now active on TV Display" : "Ticker has been disabled",
        variant: "default"
      });
      
      setShowTickerMenu(false);
    } catch (error) {
      console.error('❌ Error saving ticker settings:', error);
      toast({
        title: "Error",
        description: "Failed to save ticker settings",
        variant: "destructive"
      });
    }
  };

  const loadTickerSettings = async () => {
    if (!user?.uid) return;
    
    try {
      console.log('📺 Loading ticker settings for user:', user.uid);
      
      const docRef = doc(db, 'karaoke_ticker_settings', user.uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log('📺 Loaded ticker settings:', data);
        
        setTickerEnabled(data.enabled || false);
        setTickerText(data.text || '');
      } else {
        console.log('📺 No saved ticker settings found, using defaults');
      }
    } catch (error) {
      console.error('❌ Error loading ticker settings:', error);
    }
  };

  const saveListIsFullSettings = async () => {
    if (!user?.uid || !isLive) return;
    
    try {
      console.log('💾 Saving List Is Full settings for user:', user.uid);
      
      await updateDoc(doc(db, 'live_status', user.uid), {
        list_is_full: listIsFull,
        check_back_minutes: checkBackMinutes,
        updated_at: new Date().toISOString()
      });
      
      toast({
        title: "Settings Saved",
        description: listIsFull 
          ? `List is now full. Users will see a message to check back in ${checkBackMinutes} minutes.` 
          : "List is now open for requests.",
        variant: "default"
      });
    } catch (error) {
      console.error('❌ Error saving List Is Full settings:', error);
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive"
      });
    }
  };

  const saveDynamicPricingSettings = async () => {
    if (!user?.uid || !isLive) return;
    
    try {
      console.log('💾 Saving Dynamic Pricing settings for user:', user.uid);
      
      // Filter out the example group and blank groups, keep only valid groups
      const validGroups = dynamicPricingGroups.filter(group => 
        group.from !== null && group.to !== null && group.price !== null &&
        !(group.from === 3 && group.to === 8 && group.price === 3.00) // Exclude example
      );
      
      const dynamicPricingData = {
        enabled: dynamicPricingEnabled,
        groups: validGroups,
        updated_at: new Date().toISOString()
      };
      
      await updateDoc(doc(db, 'live_status', user.uid), {
        dynamic_pricing: dynamicPricingData,
        updated_at: new Date().toISOString()
      });
      
      toast({
        title: "Dynamic Pricing Saved",
        description: dynamicPricingEnabled 
          ? `Dynamic pricing is now active with ${validGroups.length} price group(s).` 
          : "Dynamic pricing is disabled. Using default skip queue price.",
        variant: "default"
      });
    } catch (error) {
      console.error('❌ Error saving Dynamic Pricing settings:', error);
      toast({
        title: "Error",
        description: "Failed to save dynamic pricing settings",
        variant: "destructive"
      });
    }
  };

  const addDynamicPricingGroup = () => {
    setDynamicPricingGroups([...dynamicPricingGroups, { from: null, to: null, price: null }]);
  };

  const removeDynamicPricingGroup = (index) => {
    // Don't allow removing the example group (index 0) or if it's the only group
    if (index === 0 || dynamicPricingGroups.length <= 2) return;
    const newGroups = dynamicPricingGroups.filter((_, i) => i !== index);
    setDynamicPricingGroups(newGroups);
  };

  const updateDynamicPricingGroup = (index, field, value) => {
    const newGroups = [...dynamicPricingGroups];
    newGroups[index] = { ...newGroups[index], [field]: value };
    setDynamicPricingGroups(newGroups);
  };

  const getPublicUrl = () => {
    if (!profile?.url_slug) return null;
    return buildKaraokePublicUrl(profile.url_slug);
  };

  const getShareableUrl = () => {
    if (!profile?.url_slug) return null;
    return buildKaraokePublicUrl(profile.url_slug);
  };

  const getTvDisplayUrl = () => {
    if (!profile?.url_slug) return null;
    return buildKaraokeTvUrl(profile.url_slug);
  };

  const getTvControlUrl = () => {
    if (!profile?.url_slug) return null;
    return buildKaraokeTvControlUrl(profile.url_slug);
  };

  const formatTimer = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatCountdown = (milliseconds) => {
    if (!milliseconds || milliseconds <= 0) return '0h 0m';
    
    const totalMinutes = Math.floor(milliseconds / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return `${hours}h ${minutes}m`;
  };

  // Count requests per user per session
  const getRequestCountForUser = (requesterName, sessionId) => {
    if (!requesterName) return 0;
    // Use provided sessionId or fallback to currentSession.id
    const activeSessionId = sessionId || currentSession?.id;
    if (!activeSessionId) return 0;
    
    const allRequests = [...queue, ...pendingRequests, ...historyRequests];
    const matchingRequests = allRequests.filter(req => 
      req.session_id === activeSessionId &&
      (req.requester_name?.toLowerCase() === requesterName.toLowerCase() ||
       req.name?.toLowerCase() === requesterName.toLowerCase() ||
       req.requesterName?.toLowerCase() === requesterName.toLowerCase())
    );
    return matchingRequests.length;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-xl">Loading karaoke dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Authentication Required</h1>
          <p className="text-gray-400">Please log in to access the karaoke dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Karaoke Live Dashboard - Your Song Request</title>
        <meta name="description" content="Manage your live karaoke sessions and song requests." />
      </Helmet>

      {mobile?.isMobile ? (
        <MobileKaraokeLiveDashboard
          isLive={isLive}
          currentSession={currentSession}
          queue={queue}
          pendingRequests={pendingRequests}
          historyRequests={historyRequests}
          sessionStats={sessionStats}
          onStartSession={startSession}
          onStopSession={startUndoCountdown}
          onApproveRequest={approveRequest}
          onDenyRequest={denyRequest}
          onMarkAsDone={markAsPerformed}
          onAnnounceRequest={announceUpNext}
          onRemoveFromQueue={removeSongFromQueue}
          getPublicUrl={getPublicUrl}
          profile={profile}
          currency={currency}
          format={format}
          user={user}
        />
      ) : (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-purple-100">Karaoke Live Dashboard</h1>
            <p className="text-purple-300 mt-1">Manage your live karaoke sessions and song requests</p>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* First-time Quick Guide Wizard Button */}
            {!hasSeenKldTour && (
              <Button
                onClick={() => setShowTour(true)}
                variant="outline"
                size="sm"
                className="border-green-500 text-green-400 hover:bg-green-500/20 bg-green-900/20"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                Quick Guide Wizard
              </Button>
            )}
            
            {/* Post-tour Help Button (just ?) */}
            {hasSeenKldTour && (
              <Button
                onClick={() => setShowTour(true)}
                variant="outline"
                size="sm"
                className="border-green-500 text-green-400 hover:bg-green-500/20 bg-green-900/20"
              >
                <HelpCircle className="w-4 h-4" />
              </Button>
            )}
            
            <Badge 
              variant={isLive ? "default" : "secondary"}
              className={isLive ? "bg-red-600 hover:bg-red-700" : "bg-gray-600"}
            >
              <Radio className="w-3 h-3 mr-1" />
              {isLive ? "LIVE" : "OFFLINE"}
            </Badge>
            

            
            {isLive && !undoVisible && (
              <Button
                onClick={startUndoCountdown}
                variant="destructive"
                size="sm"
                className="bg-red-600 hover:bg-red-700"
              >
                <Square className="w-4 h-4 mr-2" />
                End Session
              </Button>
            )}
            

          </div>
        </div>

        {/* Undo Countdown Top Bar */}
        {undoVisible && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-red-900/90 border border-red-600/50 backdrop-blur-sm rounded-lg p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-red-200 font-semibold">Ending Session</span>
                </div>
                <div className="text-red-300 text-sm">
                  Your karaoke session will end in <span className="text-red-100 font-bold text-lg">{undoTimer}</span> seconds
                </div>
              </div>
              <Button
                onClick={cancelUndoCountdown}
                className="bg-green-600 hover:bg-green-700 text-white"
                size="sm"
              >
                <Radio className="w-4 h-4 mr-2" />
                Stay Online
              </Button>
            </div>
          </motion.div>
        )}



        {/* Main Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Column - Session Control */}
          <div className="lg:col-span-1 space-y-6">
            
            {/* Session Control Card */}
            <Card className="bg-purple-900/20 border-purple-700">
              <CardHeader>
                <CardTitle className="text-purple-100 flex items-center">
                  <Radio className="w-5 h-5 mr-2" />
                  Session Control
                </CardTitle>
                <CardDescription className="text-purple-300">
                  Start and manage your karaoke session
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!isLive ? (
                  <Button
                    onClick={startSession}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    disabled={!profile?.url_slug}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start Karaoke Session
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <div className="text-center p-4 bg-purple-800/30 rounded-lg">
                      <p className="text-purple-200 font-semibold">Session Active</p>
                      <p className="text-purple-400 text-sm">
                        Started: {currentSession?.startTime?.toLocaleTimeString()}
                      </p>
                                              <div className="mt-3 pt-3 border-t border-purple-600/30">
                          <p className="text-purple-300 text-sm">Session Ending:</p>
                          <div className="flex items-center justify-center space-x-2 mt-1">
                            <span className="text-purple-200 font-semibold">
                              {remainingTime !== null ? formatCountdown(remainingTime) : formatTimer(sessionTimer)}
                            </span>
                            <Button
                              onClick={() => setShowTimerModal(true)}
                              size="sm"
                              variant="outline"
                              className="border-purple-600 text-purple-300 hover:bg-purple-600/20"
                            >
                              Edit
                            </Button>
                          </div>
                        </div>
                    </div>
                    
                    {/* Song Request Form Editor Button */}
                    <Button
                      onClick={() => setShowFormEditor(true)}
                      variant="outline"
                      className="w-full border-purple-600 text-purple-300 hover:bg-purple-600/20"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Edit Song Request Form
                    </Button>

                    {/* Paid Request Settings Button */}
                    <Button
                      onClick={() => setPaidRequestSettingsExpanded(!paidRequestSettingsExpanded)}
                      variant="outline"
                      className="w-full border-purple-600 text-purple-300 hover:bg-purple-600/20"
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Paid Request Settings
                      <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${paidRequestSettingsExpanded ? 'rotate-180' : ''}`} />
                    </Button>

                    {/* Expandable Paid Request Settings */}
                    {paidRequestSettingsExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-4 p-4 bg-purple-800/30 rounded-lg border border-purple-600/50 space-y-4">
                          {/* Enable/Disable Toggle */}
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-purple-200 font-semibold">Enable Paid Requests</h3>
                              <p className="text-purple-300 text-sm">
                                Allow audience members to pay to skip the queue
                              </p>
                            </div>
                            <Switch
                              checked={paidRequestSettings.enabled}
                              onCheckedChange={async (checked) => {
                                // ✅ Auto-enable showWaitTimes when paid requests are enabled
                                const newSettings = { 
                                  ...paidRequestSettings, 
                                  enabled: checked,
                                  showWaitTimes: checked ? true : paidRequestSettings.showWaitTimes // Auto-enable, but keep existing value if disabling
                                };
                                setPaidRequestSettings(newSettings);
                                
                                // Auto-save when toggle is changed
                                if (user?.uid && isLive) {
                                  try {
                                    await updateDoc(doc(db, 'live_status', user.uid), {
                                      paid_request_settings: newSettings,
                                      updated_at: new Date().toISOString()
                                    });
                                    
                                    toast({
                                      title: checked ? "Paid Requests Enabled" : "Paid Requests Disabled",
                                      description: checked 
                                        ? "Audience members can now skip the queue for a fee. Wait times are now visible." 
                                        : "Paid queue skipping is now disabled",
                                      variant: "default"
                                    });
                                    
                                    // Reload form fields to show/hide skip_queue field
                                    loadFormFields();
                                  } catch (error) {
                                    console.error('❌ Error auto-saving paid request settings:', error);
                                    toast({
                                      title: "Auto-save Failed",
                                      description: "Please manually save your settings",
                                      variant: "destructive"
                                    });
                                  }
                                }
                              }}
                              className="data-[state=checked]:bg-purple-600"
                            />
                          </div>

                          {paidRequestSettings.enabled && (
                            <>
                              {/* Skip Queue Price */}
                              <div className="space-y-2">
                                <Label htmlFor="skipQueuePrice" className="text-purple-200 text-sm">Base Skip Queue Price ({symbol()})</Label>
                                <Input
                                  id="skipQueuePrice"
                                  type="number"
                                  step="0.50"
                                  min="3.00"
                                  value={paidRequestSettings.skipQueuePrice === null || paidRequestSettings.skipQueuePrice === undefined ? '' : paidRequestSettings.skipQueuePrice}
                                  onChange={async (e) => {
                                    const inputValue = e.target.value;
                                    
                                    // Allow empty field
                                    if (inputValue === '') {
                                      setPaidRequestSettings({ ...paidRequestSettings, skipQueuePrice: null });
                                      return;
                                    }
                                    
                                    const numValue = parseFloat(inputValue);
                                    
                                    // Allow any numeric value while typing (validation happens on blur and save)
                                    if (!isNaN(numValue)) {
                                      const newSettings = { ...paidRequestSettings, skipQueuePrice: numValue };
                                      setPaidRequestSettings(newSettings);
                                      
                                      // Auto-save when price is changed (only if valid - >= 3.0 and in 0.50 increments)
                                      if (user?.uid && isLive && numValue >= 3.0) {
                                        const roundedValue = Math.round(numValue * 2) / 2;
                                        if (roundedValue === numValue) {
                                          try {
                                            await updateDoc(doc(db, 'live_status', user.uid), {
                                              paid_request_settings: newSettings,
                                              updated_at: new Date().toISOString()
                                            });
                                          } catch (error) {
                                            console.error('❌ Error auto-saving price:', error);
                                          }
                                        }
                                      }
                                    }
                                  }}
                                  onBlur={async (e) => {
                                    const inputValue = e.target.value;
                                    if (inputValue === '') {
                                      // Keep field empty, don't reset to default
                                      return;
                                    }
                                    const numValue = parseFloat(inputValue);
                                    if (isNaN(numValue) || numValue < 3.0) {
                                      // Show toast if below minimum
                                      if (!isNaN(numValue) && numValue < 3.0) {
                                        toast({
                                          title: "Minimum Price Required",
                                          description: `The Skip Queue price must be at least ${format(3)}.`,
                                          variant: "destructive"
                                        });
                                      }
                                      // If invalid, set to null (empty field)
                                      setPaidRequestSettings({ ...paidRequestSettings, skipQueuePrice: null });
                                    } else {
                                      // Round to nearest 0.50 increment
                                      const roundedValue = Math.round(numValue * 2) / 2;
                                      const finalValue = roundedValue < 3.0 ? null : roundedValue;
                                      
                                      // Show toast if rounded value is below minimum
                                      if (finalValue !== null && finalValue < 3.0) {
                                        toast({
                                          title: "Minimum Price Required",
                                          description: `The Skip Queue price must be at least ${format(3)}.`,
                                          variant: "destructive"
                                        });
                                        setPaidRequestSettings({ ...paidRequestSettings, skipQueuePrice: null });
                                        return;
                                      }
                                      
                                      setPaidRequestSettings({ ...paidRequestSettings, skipQueuePrice: finalValue });
                                      
                                      // Auto-save the rounded value if valid
                                      if (user?.uid && isLive && finalValue !== null && finalValue >= 3.0) {
                                        try {
                                          await updateDoc(doc(db, 'live_status', user.uid), {
                                            paid_request_settings: { ...paidRequestSettings, skipQueuePrice: finalValue },
                                            updated_at: new Date().toISOString()
                                          });
                                        } catch (error) {
                                          console.error('❌ Error auto-saving price:', error);
                                        }
                                      }
                                    }
                                  }}
                                  className="bg-purple-800/30 border-purple-600 text-purple-100 h-8 text-sm"
                                  placeholder="3.00"
                                />
                                <p className="text-purple-400 text-xs">
                                  Minimum: {format(3.00)}
                                </p>
                              </div>

                              {/* Maximum Paid Requests */}
                              <div className="space-y-2">
                                <Label htmlFor="maxPaidRequests" className="text-purple-200 text-sm">Max Concurrent Paid Requests</Label>
                                <Input
                                  id="maxPaidRequests"
                                  type="number"
                                  min="1"
                                  value={paidRequestSettings.maxPaidRequests}
                                  onChange={async (e) => {
                                    const newMax = parseInt(e.target.value) || 1;
                                    const newSettings = { ...paidRequestSettings, maxPaidRequests: newMax };
                                    setPaidRequestSettings(newSettings);
                                    
                                    // Auto-save when max requests is changed
                                    if (user?.uid && isLive) {
                                      try {
                                        await updateDoc(doc(db, 'live_status', user.uid), {
                                          paid_request_settings: newSettings,
                                          updated_at: new Date().toISOString()
                                        });
                                      } catch (error) {
                                        console.error('❌ Error auto-saving max requests:', error);
                                      }
                                    }
                                  }}
                                  className="bg-purple-800/30 border-purple-600 text-purple-100 h-8 text-sm"
                                  placeholder="3"
                                />
                              </div>

                              {/* Show Wait Times Toggle */}
                              <div className="flex items-center justify-between p-3 bg-purple-800/20 rounded-lg border border-purple-600/30">
                                <div>
                                  <Label htmlFor="showWaitTimes" className="text-purple-200 text-sm font-semibold cursor-pointer">
                                    Show Wait Times
                                  </Label>
                                  <p className="text-purple-300 text-xs mt-1">
                                    Display estimated wait times to audience members
                                  </p>
                                </div>
                                <Switch
                                  id="showWaitTimes"
                                  checked={paidRequestSettings.showWaitTimes}
                                  onCheckedChange={async (checked) => {
                                    const newSettings = { ...paidRequestSettings, showWaitTimes: checked };
                                    setPaidRequestSettings(newSettings);
                                    
                                    // Auto-save when toggle is changed
                                    if (user?.uid && isLive) {
                                      try {
                                        await updateDoc(doc(db, 'live_status', user.uid), {
                                          paid_request_settings: newSettings,
                                          updated_at: new Date().toISOString()
                                        });
                                      } catch (error) {
                                        console.error('❌ Error auto-saving show wait times:', error);
                                      }
                                    }
                                  }}
                                  className="data-[state=checked]:bg-purple-600"
                                />
                              </div>

                              {/* Paid Requests Only Toggle */}
                              <div className="flex items-center justify-between p-3 bg-purple-800/20 rounded-lg border border-purple-600/30">
                                <div>
                                  <Label htmlFor="paidRequestsOnly" className="text-purple-200 text-sm font-semibold cursor-pointer">
                                    Paid Requests Only
                                  </Label>
                                  <p className="text-purple-300 text-xs mt-1">
                                    Only accept paid requests going forward (existing free requests remain)
                                  </p>
                                </div>
                                <Switch
                                  id="paidRequestsOnly"
                                  checked={paidRequestSettings.paidRequestsOnly}
                                  onCheckedChange={async (checked) => {
                                    const newSettings = { ...paidRequestSettings, paidRequestsOnly: checked };
                                    setPaidRequestSettings(newSettings);
                                    
                                    // Auto-save when toggle is changed
                                    if (user?.uid && isLive) {
                                      try {
                                        await updateDoc(doc(db, 'live_status', user.uid), {
                                          paid_request_settings: newSettings,
                                          updated_at: new Date().toISOString()
                                        });
                                        
                                        toast({
                                          title: checked ? "Paid Requests Only Enabled" : "Free Requests Enabled",
                                          description: checked 
                                            ? "Only paid requests will be accepted. Existing free requests remain in queue." 
                                            : "Both free and paid requests are now accepted",
                                          variant: "default"
                                        });
                                      } catch (error) {
                                        console.error('❌ Error auto-saving paid requests only:', error);
                                      }
                                    }
                                  }}
                                  className="data-[state=checked]:bg-purple-600"
                                />
                              </div>

                              {/* Current Paid Requests Count */}
                              <div className="p-3 bg-purple-800/20 rounded-lg border border-purple-600/30">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <h4 className="text-purple-200 font-semibold text-sm">Current Paid Requests</h4>
                                    <p className="text-purple-300 text-xs">
                                      {queue.filter(song => song.skip_queue).length} / {paidRequestSettings.maxPaidRequests}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-purple-200 font-semibold text-sm">
                                      {format(queue.filter(song => song.skip_queue).reduce((sum, song) => {
                                        const amount = Number(song.amount_paid);
                                        if (!isNaN(amount) && amount > 0) {
                                          return sum + amount;
                                        }
                                        const fallbackPrice = Number(paidRequestSettings.skipQueuePrice) || 0;
                                        return sum + fallbackPrice;
                                      }, 0))}
                                    </p>
                                    <p className="text-purple-400 text-xs">Total Earnings</p>
                                  </div>
                                </div>
                              </div>

                              {/* Save Button */}
                              <Button
                                onClick={savePaidRequestSettings}
                                size="sm"
                                disabled={
                                  paidRequestSettings.skipQueuePrice === null || 
                                  paidRequestSettings.skipQueuePrice === undefined || 
                                  paidRequestSettings.skipQueuePrice === '' || 
                                  isNaN(paidRequestSettings.skipQueuePrice) || 
                                  paidRequestSettings.skipQueuePrice < 3.0
                                }
                                className="w-full bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Save Settings
                              </Button>
                            </>
                          )}
                        </div>
                      </motion.div>
                    )}

                    {/* List Is Full Settings */}
                    <div className="mt-4 p-4 bg-purple-800/30 rounded-lg border border-purple-600/50 space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-purple-200 font-semibold">List Is Full</h3>
                          <p className="text-purple-300 text-sm">
                            When enabled, only paid requests are accepted. Free requests will see a message to check back later.
                          </p>
                        </div>
                        <Switch
                          checked={listIsFull}
                          onCheckedChange={(checked) => {
                            setListIsFull(checked);
                            // Auto-save when toggle is changed
                            if (user?.uid && isLive) {
                              updateDoc(doc(db, 'live_status', user.uid), {
                                list_is_full: checked,
                                check_back_minutes: checkBackMinutes,
                                updated_at: new Date().toISOString()
                              }).then(() => {
                                toast({
                                  title: checked ? "List Is Full Enabled" : "List Is Open",
                                  description: checked 
                                    ? `Only paid requests accepted. Users will see message to check back in ${checkBackMinutes} minutes.` 
                                    : "All requests are now accepted.",
                                  variant: "default"
                                });
                              }).catch((error) => {
                                console.error('❌ Error saving list is full setting:', error);
                              });
                            }
                          }}
                          className="data-[state=checked]:bg-purple-600"
                        />
                      </div>

                      {listIsFull && (
                        <div className="space-y-2">
                          <Label htmlFor="checkBackMinutes" className="text-purple-200 text-sm">
                            Check Back In (Minutes)
                          </Label>
                          <Input
                            id="checkBackMinutes"
                            type="number"
                            min="1"
                            max="120"
                            value={checkBackMinutes}
                            onChange={(e) => {
                              const minutes = parseInt(e.target.value) || 15;
                              setCheckBackMinutes(minutes);
                              // Auto-save when minutes is changed
                              if (user?.uid && isLive) {
                                updateDoc(doc(db, 'live_status', user.uid), {
                                  list_is_full: listIsFull,
                                  check_back_minutes: minutes,
                                  updated_at: new Date().toISOString()
                                }).catch((error) => {
                                  console.error('❌ Error saving check back minutes:', error);
                                });
                              }
                            }}
                            className="bg-purple-800/30 border-purple-600 text-purple-100 h-8 text-sm"
                            placeholder="15"
                          />
                          <p className="text-purple-400 text-xs">
                            Users will be told to check back in {checkBackMinutes} minutes
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Dynamic Pricing Settings */}
                    <div className="mt-4 p-4 bg-purple-800/30 rounded-lg border border-purple-600/50 space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-purple-200 font-semibold">Dynamic Pricing</h3>
                          <p className="text-purple-300 text-sm">
                            Set different prices based on queue position. When disabled, uses default skip queue price.
                          </p>
                        </div>
                        <Switch
                          checked={dynamicPricingEnabled}
                          onCheckedChange={(checked) => {
                            setDynamicPricingEnabled(checked);
                          }}
                          className="data-[state=checked]:bg-purple-600"
                        />
                      </div>

                      {dynamicPricingEnabled && (
                        <div className="space-y-4">
                          {/* Example Group (Greyed Out) */}
                          <div className="p-3 bg-gray-800/30 rounded-lg border border-gray-600/30 opacity-60">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-gray-400 text-xs font-semibold">Example (for reference)</span>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                              <div>
                                <Label className="text-gray-400 text-xs">From Song #</Label>
                                <Input
                                  value="3"
                                  disabled
                                  className="bg-gray-700/50 border-gray-600 text-gray-400 h-8 text-sm"
                                />
                              </div>
                              <div>
                                <Label className="text-gray-400 text-xs">To Song #</Label>
                                <Input
                                  value="8"
                                  disabled
                                  className="bg-gray-700/50 border-gray-600 text-gray-400 h-8 text-sm"
                                />
                              </div>
                              <div>
                                <Label className="text-gray-400 text-xs">Price</Label>
                                <Input
                                  value="3.00"
                                  disabled
                                  className="bg-gray-700/50 border-gray-600 text-gray-400 h-8 text-sm"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Dynamic Pricing Groups */}
                          {dynamicPricingGroups.map((group, index) => {
                            // Skip the example group (index 0) in the editable list
                            if (index === 0) return null;
                            
                            const isBlank = group.from === null && group.to === null && group.price === null;
                            
                            return (
                              <div key={index} className="p-3 bg-purple-800/20 rounded-lg border border-purple-600/30">
                                <div className="grid grid-cols-3 gap-3">
                                  <div>
                                    <Label className="text-purple-200 text-xs">From Song #</Label>
                                    <Select
                                      value={group.from?.toString() || ''}
                                      onValueChange={(value) => updateDynamicPricingGroup(index, 'from', parseInt(value))}
                                    >
                                      <SelectTrigger className="bg-purple-800/50 border-purple-600 text-purple-100 h-8 text-sm">
                                        <SelectValue placeholder="Select" />
                                      </SelectTrigger>
                                      <SelectContent className="bg-purple-900 border-purple-600">
                                        {Array.from({ length: 20 }, (_, i) => i + 1).map(num => (
                                          <SelectItem key={num} value={num.toString()}>
                                            {num}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label className="text-purple-200 text-xs">To Song #</Label>
                                    <Select
                                      value={group.to?.toString() || ''}
                                      onValueChange={(value) => updateDynamicPricingGroup(index, 'to', parseInt(value))}
                                    >
                                      <SelectTrigger className="bg-purple-800/50 border-purple-600 text-purple-100 h-8 text-sm">
                                        <SelectValue placeholder="Select" />
                                      </SelectTrigger>
                                      <SelectContent className="bg-purple-900 border-purple-600">
                                        {Array.from({ length: 20 }, (_, i) => i + 1).map(num => (
                                          <SelectItem key={num} value={num.toString()}>
                                            {num}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label className="text-purple-200 text-xs">Price ({symbol})</Label>
                                    <div className="flex items-center space-x-2">
                                      <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={group.price || ''}
                                        onChange={(e) => updateDynamicPricingGroup(index, 'price', parseFloat(e.target.value) || null)}
                                        placeholder="0.00"
                                        className="bg-purple-800/50 border-purple-600 text-purple-100 h-8 text-sm"
                                      />
                                      {!isBlank && dynamicPricingGroups.length > 2 && (
                                        <Button
                                          onClick={() => removeDynamicPricingGroup(index)}
                                          size="sm"
                                          variant="destructive"
                                          className="h-8 w-8 p-0"
                                        >
                                          <X className="w-4 h-4" />
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                          {/* Add Another Price Group Button */}
                          <Button
                            onClick={addDynamicPricingGroup}
                            variant="outline"
                            size="sm"
                            className="w-full border-purple-600 text-purple-300 hover:bg-purple-600/20"
                          >
                            <Music className="w-4 h-4 mr-2" />
                            Add Another Price Group
                          </Button>

                          {/* Save Button */}
                          <Button
                            onClick={saveDynamicPricingSettings}
                            size="sm"
                            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                          >
                            Save Dynamic Pricing
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Links Card */}
            <Card className="bg-purple-900/20 border-purple-700">
              <CardHeader>
                <CardTitle className="text-purple-100 flex items-center">
                  <ExternalLink className="w-5 h-5 mr-2" />
                  Quick Links
                </CardTitle>
                <CardDescription className="text-purple-300">
                  Access your karaoke pages
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {getPublicUrl() && (
                  <Button
                    onClick={() => window.open(getPublicUrl(), '_blank')}
                    variant="outline"
                    className="w-full border-purple-600 text-purple-300 hover:bg-purple-600/20"
                  >
                    <QrCode className="w-4 h-4 mr-2" />
                    Public Request Page
                  </Button>
                )}
                
                {getTvDisplayUrl() && (
                  <Button
                    onClick={() => window.open(getTvDisplayUrl(), '_blank')}
                    variant="outline"
                    className="w-full border-purple-600 text-purple-300 hover:bg-purple-600/20"
                  >
                    <Maximize2 className="w-4 h-4 mr-2" />
                    TV Display
                  </Button>
                )}
                
                {getTvControlUrl() && (
                  <Button
                    onClick={() => window.open(getTvControlUrl(), '_blank')}
                    variant="outline"
                    className="w-full border-purple-600 text-purple-300 hover:bg-purple-600/20"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    TV Control Panel
                  </Button>
                )}
                
                {/* Small QR Request Window Button */}
                {profile?.url_slug && (
                  <Button
                    onClick={() => {
                      const qrWindow = window.open(
                        '',
                        'SmallQRWindow',
                        'width=400,height=600,scrollbars=no,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
                      );
                      if (qrWindow) {
                        const qrUrl = getShareableUrl();
                        if (!qrUrl) {
                          qrWindow.close();
                          toast({
                            title: "Missing URL",
                            description: "Unable to generate QR code because the public URL is unavailable.",
                            variant: "destructive"
                          });
                          return;
                        }
                        qrWindow.document.write(`
                          <!DOCTYPE html>
                          <html lang="en">
                          <head>
                            <meta charset="UTF-8">
                            <meta name="viewport" content="width=device-width, initial-scale=1.0">
                            <title>QR Request Window</title>
                            <style>
                              * { margin: 0; padding: 0; box-sizing: border-box; }
                              body {
                                font-family: Inter, sans-serif;
                                background: linear-gradient(to bottom right, #581c87, #7c3aed, #4c1d95);
                                min-height: 100vh;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                padding: 20px;
                              }
                              .container {
                                background: rgba(0, 0, 0, 0.3);
                                backdrop-filter: blur(10px);
                                border: 2px solid rgba(168, 85, 247, 0.5);
                                border-radius: 12px;
                                padding: 24px;
                                text-align: center;
                                max-width: 100%;
                              }
                              h1 {
                                color: #e9d5ff;
                                font-size: 20px;
                                margin-bottom: 8px;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                gap: 8px;
                              }
                              p {
                                color: #c4b5fd;
                                font-size: 14px;
                                margin-bottom: 16px;
                              }
                              .qr-container {
                                background: white;
                                padding: 16px;
                                border-radius: 8px;
                                display: inline-block;
                                margin-bottom: 16px;
                              }
                              .qr-container img {
                                display: block;
                                max-width: 100%;
                                height: auto;
                              }
                              .instruction {
                                color: #c4b5fd;
                                font-size: 12px;
                                margin-bottom: 16px;
                              }
                              .footer {
                                margin-top: 16px;
                                padding-top: 16px;
                                border-top: 1px solid rgba(168, 85, 247, 0.3);
                              }
                              .footer-text {
                                color: #c4b5fd;
                                font-size: 14px;
                                font-weight: 500;
                                margin-bottom: 4px;
                              }
                              .footer-brand {
                                color: #e9d5ff;
                                font-size: 16px;
                                font-weight: bold;
                              }
                            </style>
                          </head>
                          <body>
                            <div class="container">
                              <h1>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                  <path d="M9 9h6v6H9z"/>
                                </svg>
                                Request Songs
                              </h1>
                              <p>Scan this QR code to request songs</p>
                              <div class="qr-container">
                                <img src="https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qrUrl)}" alt="QR Code" />
                              </div>
                              <p class="instruction">Point your phone camera at this QR code to open the song request page</p>
                              <div class="footer">
                                <div class="footer-text">Brought to you by:</div>
                                <div class="footer-brand">YourSongRequest.com</div>
                              </div>
                            </div>
                          </body>
                          </html>
                        `);
                        qrWindow.document.close();
                      } else {
                        toast({
                          title: "Popup Blocked",
                          description: "Please allow popups to open the QR window",
                          variant: "destructive"
                        });
                      }
                    }}
                    variant="outline"
                    className="w-full border-purple-600 text-purple-300 hover:bg-purple-600/20"
                  >
                    <QrCode className="w-4 h-4 mr-2" />
                    <span className="text-left">
                      Small QR Request Window
                      <br />
                      <span className="text-xs">(for TV Displays)</span>
                    </span>
                  </Button>
                )}
                
                {/* TV Display Ticker Button */}
                <div className="relative ticker-menu-container">
                  <Button
                    onClick={() => setShowTickerMenu(!showTickerMenu)}
                    variant="outline"
                    className="w-full border-purple-600 text-purple-300 hover:bg-purple-600/20"
                  >
                    <ScrollText className="w-4 h-4 mr-2" />
                    TV Display Ticker
                  </Button>
                  
                  {/* Ticker Menu Dropdown */}
                  {showTickerMenu && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-purple-900 border border-purple-600 rounded-lg p-4 z-10">
                      <div className="space-y-4">
                        {/* Toggle Switch */}
                        <div className="flex items-center justify-between">
                          <Label htmlFor="ticker-toggle" className="text-purple-200 text-sm">
                            Ticker Enabled
                          </Label>
                          <Switch
                            id="ticker-toggle"
                            checked={tickerEnabled}
                            onCheckedChange={setTickerEnabled}
                            className="data-[state=checked]:bg-purple-600"
                          />
                        </div>
                        
                        {/* Text Input */}
                        <div className="space-y-2">
                          <Label htmlFor="ticker-text" className="text-purple-200 text-sm">
                            Ticker Text
                          </Label>
                          <Textarea
                            id="ticker-text"
                            value={tickerText}
                            onChange={(e) => setTickerText(e.target.value)}
                            placeholder="Enter ticker text to display on TV..."
                            className="bg-purple-800 border-purple-600 text-purple-100 placeholder-purple-400"
                            rows={3}
                          />
                        </div>
                        
                        {/* Save Button */}
                        <Button
                          onClick={saveTickerSettings}
                          className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                          disabled={!tickerText.trim()}
                        >
                          Save Ticker Settings
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Sharing Options Card */}
            {isLive && getPublicUrl() && (
              <Card className="bg-purple-900/20 border-purple-700">
                <CardHeader>
                  <CardTitle className="text-purple-100 flex items-center">
                    <QrCode className="w-5 h-5 mr-2" />
                    Sharing Options
                  </CardTitle>
                  <CardDescription className="text-purple-300">
                    Share QR code or URL with your audience
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-center space-y-4">
                  {/* QR Code Section */}
                  <div>
                    <h4 className="text-purple-200 font-semibold mb-2">QR Code</h4>
                    <div className="bg-white p-4 rounded-lg inline-block">
                      <QRCode value={getPublicUrl()} size={150} />
                    </div>
                    <p className="text-purple-400 text-sm mt-2">
                      Scan to join the karaoke session
                    </p>
                  </div>
                  
                  {/* URL Section */}
                  <div className="border-t border-purple-700 pt-4">
                    <h4 className="text-purple-200 font-semibold mb-2">Direct URL</h4>
                    <div className="bg-purple-800/50 p-3 rounded-lg">
                      <p className="text-purple-200 font-mono text-sm break-all">
                        {getShareableUrl()}
                      </p>
                    </div>
                    <p className="text-purple-400 text-sm mt-2">
                      Share this link with your audience
                    </p>
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(getShareableUrl());
                        toast({
                          title: "URL Copied!",
                          description: "The karaoke URL has been copied to your clipboard",
                          variant: "default"
                        });
                      }}
                      size="sm"
                      className="mt-2 bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Copy URL
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Queue & Requests */}
          <div className="lg:col-span-2 space-y-6">

            {/* Incoming Request Queue Card */}
            <Card className="bg-purple-900/20 border-purple-700">
              <CardHeader>
                <CardTitle className="text-purple-100 flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  Incoming Request Queue
                </CardTitle>
                <CardDescription className="text-purple-300">
                  Approved songs appear here. Select 'Done' when complete.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {queue.length > 0 ? (
                  <div className="space-y-0 max-h-96 overflow-y-auto">
                    {queue.map((song, index) => (
                      <div
                        key={index}
                        className="relative py-4 px-3 border-b border-purple-600/20 last:border-b-0 hover:bg-purple-800/20 transition-colors"
                      >
                        {/* Top border for first item */}
                        {index === 0 && <div className="absolute top-0 left-0 right-0 h-px bg-purple-600/20"></div>}
                        
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3 flex-1 min-w-0">
                            <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center relative flex-shrink-0">
                              <span className="text-white text-sm font-bold">{index + 1}</span>
                              {song.skip_queue && (
                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center">
                                  <Star className="w-3 h-3 text-white" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              {/* Song Title Row */}
                              <div className="flex items-center space-x-2 mb-1">
                                <p className="text-purple-100 font-semibold text-lg truncate">{capitalizeWords(song.song_title)}</p>
                                {song.skip_queue && (
                                  <Badge variant="secondary" className="bg-yellow-600 text-white text-xs flex-shrink-0">
                                    Paid
                                  </Badge>
                                )}
                              </div>
                              
                              {/* Artist Row */}
                              <p className="text-purple-400 text-sm mb-2">{capitalizeWords(song.artist_name)}</p>
                              
                              {/* Requester Row */}
                              <div className="flex items-center space-x-2 mb-1">
                                <p className="text-purple-300 text-sm">
                                  Requested by: {capitalizeWords(song.requester_name || song.name || song.requesterName || 'Anonymous')}
                                </p>
                                {currentSession?.id && (() => {
                                  const requesterName = song.requester_name || song.name || song.requesterName;
                                  const requestCount = getRequestCountForUser(requesterName, currentSession.id);
                                  if (requestCount > 1) {
                                    return (
                                      <Badge variant="secondary" className="bg-blue-600 text-white text-xs">
                                        {requestCount}{requestCount === 2 ? 'nd' : requestCount === 3 ? 'rd' : 'th'} request
                                      </Badge>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                              
                              {/* Checkbox Fields Row */}
                              {formFields
                                .filter(field => field.type === 'checkbox' && field.id !== 'skip_queue')
                                .map(field => {
                                  const fieldValue = song[field.id];
                                  const isChecked = fieldValue === true || fieldValue === 'true' || fieldValue === 1;
                                  return (
                                    <p key={field.id} className="text-purple-300 text-xs mb-1">
                                      {field.label}: {isChecked ? 'ticked' : 'unticked'}
                                    </p>
                                  );
                                })}
                              
                              {/* Note Row */}
                              {(song.note || song.comment || song.notes || song.comments) && (
                                <p className="text-purple-400 text-xs mb-2 italic">
                                  Note: {song.note || song.comment || song.notes || song.comments}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                            <Button
                              onClick={() => markAsPerformed(song)}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 text-xs font-medium"
                            >
                              ✓ Done
                            </Button>
                          <DropdownMenu 
                            open={dropdownOpenStates[song.id] || false}
                            onOpenChange={(open) => {
                              // Only allow closing if not in remove confirmation state
                              if (!open && removeConfirmationId === song.id) {
                                return; // Prevent closing during confirmation
                              }
                              setDropdownOpenStates(prev => ({ ...prev, [song.id]: open }));
                            }}
                          >
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-purple-600 text-purple-300 hover:bg-purple-600/20"
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-purple-900 border-purple-600 text-white">
                              <DropdownMenuItem
                                onClick={() => {
                                  moveSongUp(index);
                                  setDropdownOpenStates(prev => ({ ...prev, [song.id]: false }));
                                }}
                                disabled={index === 0}
                                className="text-purple-200 hover:bg-purple-800 cursor-pointer"
                              >
                                <ArrowUp className="w-4 h-4 mr-2" />
                                Move Up
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  moveSongDown(index);
                                  setDropdownOpenStates(prev => ({ ...prev, [song.id]: false }));
                                }}
                                disabled={index === queue.length - 1}
                                className="text-purple-200 hover:bg-purple-800 cursor-pointer"
                              >
                                <ArrowDown className="w-4 h-4 mr-2" />
                                Move Down
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedSong(song);
                                  setShowPositionModal(true);
                                  setDropdownOpenStates(prev => ({ ...prev, [song.id]: false }));
                                }}
                                className="text-purple-200 hover:bg-purple-800 cursor-pointer"
                              >
                                <ArrowRight className="w-4 h-4 mr-2" />
                                Move to position...
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  console.log('🎤 Announce button clicked for song:', song);
                                  console.log('🎤 Song requester_name:', song.requester_name);
                                  console.log('🎤 Full song object:', JSON.stringify(song, null, 2));
                                  announceUpNext(song);
                                  setDropdownOpenStates(prev => ({ ...prev, [song.id]: false }));
                                }}
                                className="text-purple-200 hover:bg-purple-800 cursor-pointer"
                              >
                                <Radio className="w-4 h-4 mr-2" />
                                Announce Up Next
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => removeSongFromQueue(song)}
                                className={`cursor-pointer ${
                                  removeConfirmationId === song.id 
                                    ? 'text-red-200 hover:bg-red-800 bg-red-900/50' 
                                    : 'text-purple-200 hover:bg-purple-800'
                                }`}
                              >
                                <X className="w-4 h-4 mr-2" />
                                {removeConfirmationId === song.id ? 'Remove' : 'Remove from Queue'}
                              </DropdownMenuItem>
                              {removeConfirmationId === song.id && (
                                <DropdownMenuItem
                                  onClick={() => {
                                    cancelRemoveConfirmation();
                                    setDropdownOpenStates(prev => ({ ...prev, [song.id]: false }));
                                  }}
                                  className="text-green-200 hover:bg-green-800 cursor-pointer"
                                >
                                  <Check className="w-4 h-4 mr-2" />
                                  Cancel
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                ) : (
                  <div className="text-center p-8 bg-purple-800/20 rounded-lg">
                    <Users className="w-12 h-12 text-purple-400 mx-auto mb-4" />
                    <p className="text-purple-300">No approved songs</p>
                    <p className="text-purple-400 text-sm mt-2">
                      Approve requests to see them here
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pending Requests Card */}
            {isLive && (
              <Card className="bg-purple-900/20 border-purple-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                  <CardTitle className="text-purple-100 flex items-center">
                    <Clock className="w-5 h-5 mr-2" />
                    Pending Requests
                  </CardTitle>
                  <CardDescription className="text-purple-300">
                    Review and approve song requests from your audience
                  </CardDescription>
                    </div>
                    <Button
                      onClick={() => setShowManualEntry(true)}
                      size="sm"
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <Music className="w-4 h-4 mr-2" />
                      Manual Entry
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {pendingRequests.length > 0 ? (
                    <div className="space-y-0 max-h-96 overflow-y-auto relative">
                      {pendingRequests.slice(0, 6).map((request, index) => (
                        <div
                          key={request.id}
                          className="relative py-4 px-3 border-b border-yellow-600/20 last:border-b-0 hover:bg-yellow-900/10 transition-colors"
                        >
                          {/* Top border for first item */}
                          {index === 0 && <div className="absolute top-0 left-0 right-0 h-px bg-yellow-600/20"></div>}
                          
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              {/* Song Title Row */}
                              <div className="flex items-center space-x-2 mb-1">
                                <h4 className="text-yellow-200 font-semibold text-lg truncate">{capitalizeWords(request.song_title)}</h4>
                                {request.skip_queue && (
                                  <Badge variant="secondary" className="bg-yellow-600 text-white text-xs flex-shrink-0">
                                    Paid
                                  </Badge>
                                )}
                              </div>
                              
                              {/* Artist Row */}
                              <p className="text-yellow-300 text-sm mb-2">by {capitalizeWords(request.artist_name)}</p>
                              
                              {/* Requester Row */}
                              <div className="flex items-center space-x-2 mb-1">
                                <p className="text-yellow-400 text-sm">
                                  Requested by: {capitalizeWords(request.requester_name || request.name || request.requesterName || 'Anonymous')}
                                </p>
                                {currentSession?.id && (() => {
                                  const requesterName = request.requester_name || request.name || request.requesterName;
                                  const requestCount = getRequestCountForUser(requesterName, currentSession.id);
                                  if (requestCount > 1) {
                                    return (
                                      <Badge variant="secondary" className="bg-blue-600 text-white text-xs">
                                        {requestCount}{requestCount === 2 ? 'nd' : requestCount === 3 ? 'rd' : 'th'} request
                                      </Badge>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                              
                              {/* Note Row */}
                              {(request.note || request.comment || request.notes || request.comments) && (
                                <p className="text-yellow-400 text-xs mb-2 italic">
                                  Note: {request.note || request.comment || request.notes || request.comments}
                                </p>
                              )}
                              
                              {/* Checkbox Fields Row */}
                              {formFields
                                .filter(field => field.type === 'checkbox' && field.id !== 'skip_queue')
                                .map(field => {
                                  const fieldValue = request[field.id];
                                  const isChecked = fieldValue === true || fieldValue === 'true' || fieldValue === 1;
                                  return (
                                    <div key={field.id} className="flex items-center space-x-2 mb-1">
                                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                        isChecked 
                                          ? 'bg-yellow-500 border-yellow-500' 
                                          : 'bg-transparent border-yellow-400/50'
                                      }`}>
                                        {isChecked && (
                                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                          </svg>
                                        )}
                                      </div>
                                      <span className={`text-xs ${isChecked ? 'text-yellow-200' : 'text-yellow-400/70'}`}>
                                        {field.label}: {isChecked ? '✓ Ticked' : '✗ Unticked'}
                                      </span>
                                    </div>
                                  );
                                })}
                              
                              {/* Price Row */}
                              {request.skip_queue && (
                                <p className="text-yellow-300 text-sm font-semibold bg-yellow-400/10 px-2 py-1 rounded-full inline-block">
                                  💰 Paid Request - {format(request.amount_paid || paidRequestSettings.skipQueuePrice || 0)}
                                </p>
                              )}
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                              <Button
                                onClick={() => approveRequest(request.id)}
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 text-xs font-medium"
                              >
                                ✓ Approve
                              </Button>
                              <Button
                                onClick={() => denyRequest(request.id)}
                                size="sm"
                                variant="destructive"
                                className="bg-red-600 hover:bg-red-700 px-3 py-1.5 text-xs font-medium"
                              >
                                ✕ Deny
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {pendingRequests.length > 6 && (
                        <p className="text-purple-400 text-center text-sm">
                          +{pendingRequests.length - 6} more pending requests
                        </p>
                      )}
                      {pendingRequests.length > 6 && (
                        <div className="absolute bottom-2 right-2 bg-purple-800/80 rounded-full p-1">
                          <ChevronDown className="w-4 h-4 text-purple-300" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center p-6 bg-purple-800/20 rounded-lg">
                      <Clock className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                      <p className="text-purple-300">No pending requests</p>
                      <p className="text-purple-400 text-sm mt-1">
                        New requests will appear here
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* History Card */}
            {isLive && (
              <Card className="bg-purple-900/20 border-purple-700">
                <CardHeader>
                  <CardTitle className="text-purple-100 flex items-center">
                    <History className="w-5 h-5 mr-2" />
                    History
                  </CardTitle>
                  <CardDescription className="text-purple-300">
                    Denied, removed from queue, and performed songs this session
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {historyRequests.length > 0 ? (
                    <div className="space-y-0 max-h-96 overflow-y-auto">
                      {historyRequests.map((request, index) => {
                        const statusColor =
                          request.status === 'performed'
                            ? 'text-green-400'
                            : request.status === 'denied'
                              ? 'text-red-400'
                              : request.status === 'removed'
                                ? 'text-amber-400'
                                : 'text-blue-400';
                        const statusBg =
                          request.status === 'performed'
                            ? 'bg-green-900/30 border-green-600/30'
                            : request.status === 'denied'
                              ? 'bg-red-900/30 border-red-600/30'
                              : request.status === 'removed'
                                ? 'bg-amber-900/30 border-amber-600/30'
                                : 'bg-blue-900/30 border-blue-600/30';
                        
                        return (
                          <div
                            key={request.id}
                            className={`relative py-3 px-3 border-b border-purple-600/20 last:border-b-0 hover:bg-purple-800/20 transition-colors ${statusBg}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2 mb-1">
                                  <h4 className="text-purple-100 font-semibold text-sm truncate">
                                    {capitalizeWords(request.song_title)}
                                  </h4>
                                  <Badge 
                                    variant="secondary" 
                                    className={`text-xs flex-shrink-0 ${
                                      request.status === 'performed'
                                        ? 'bg-green-600 text-white'
                                        : request.status === 'denied'
                                          ? 'bg-red-600 text-white'
                                          : request.status === 'removed'
                                            ? 'bg-amber-600 text-white'
                                            : 'bg-blue-600 text-white'
                                    }`}
                                  >
                                    {request.status === 'performed'
                                      ? 'Played'
                                      : request.status === 'denied'
                                        ? 'Denied'
                                        : request.status === 'removed'
                                          ? 'Removed'
                                          : request.status}
                                  </Badge>
                                </div>
                                <p className="text-purple-300 text-xs mb-1">
                                  by {capitalizeWords(request.artist_name)}
                                </p>
                                <p className="text-purple-400 text-xs mb-1">
                                  Requested by: {capitalizeWords(request.requester_name || request.name || request.requesterName || 'Anonymous')}
                                </p>
                                {request.amount_paid && (
                                  <p className="text-yellow-300 text-xs mb-1 font-semibold">
                                    Paid {format(Number(request.amount_paid) || 0)}
                                  </p>
                                )}
                                {request.note && (
                                  <p className="text-purple-400 text-xs italic">
                                    Note: {request.note}
                                  </p>
                                )}
                              </div>
                              <Button
                                onClick={() => reAddRequest(request.id)}
                                size="sm"
                                variant="outline"
                                className="ml-3 border-purple-600 text-purple-300 hover:bg-purple-600/20 flex-shrink-0"
                              >
                                <RotateCcw className="w-3 h-3 mr-1" />
                                Re-Add
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center p-6 bg-purple-800/20 rounded-lg">
                      <History className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                      <p className="text-purple-300">No history yet</p>
                      <p className="text-purple-400 text-sm mt-1">
                        Denied, removed, and performed requests will appear here
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Session Stats Card */}
            <Card className="bg-purple-900/20 border-purple-700">
              <CardHeader>
                <CardTitle className="text-purple-100 flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  Session Statistics
                </CardTitle>
                <CardDescription className="text-purple-300">
                  Real-time session metrics
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-purple-800/30 rounded-lg">
                    <p className="text-2xl font-bold text-purple-100">
                      {sessionStats.totalRequests}
                    </p>
                    <p className="text-purple-400 text-sm">Total Requests</p>
                  </div>
                  <div className="text-center p-4 bg-purple-800/30 rounded-lg">
                    <p className="text-2xl font-bold text-purple-100">
                      {sessionStats.songsPerformed}
                    </p>
                    <p className="text-purple-400 text-sm">Songs Performed</p>
                  </div>
                  <div className="text-center p-4 bg-purple-800/30 rounded-lg">
                    <p className="text-2xl font-bold text-purple-100">
                      {format(sessionStats.totalEarnings)}
                    </p>
                    <p className="text-purple-400 text-sm">Total Earnings</p>
                  </div>
                  <div className="text-center p-4 bg-purple-800/30 rounded-lg">
                    <p className="text-2xl font-bold text-purple-100">
                      {sessionStats.sessionTime}m
                    </p>
                    <p className="text-purple-400 text-sm">Session Time</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      )}

      {/* Session Timer Modal */}
      <Dialog open={showTimerModal} onOpenChange={setShowTimerModal}>
        <DialogContent className="bg-purple-900 border-purple-600 text-white max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="text-purple-100">Session Timer</DialogTitle>
            <DialogDescription className="text-purple-300">
              Set how long your karaoke session will run. The timer can be edited later in Session Control.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="timer" className="text-purple-200">Session Duration</Label>
              <div className="flex items-center space-x-2 mt-2">
                <Input
                  id="timer"
                  type="number"
                  min="15"
                  max="480"
                  value={sessionTimer}
                  onChange={(e) => setSessionTimer(parseInt(e.target.value) || 120)}
                  className="bg-purple-800 border-purple-600 text-white"
                />
                <span className="text-purple-300">minutes</span>
              </div>
              <p className="text-purple-400 text-sm mt-1">
                Current: {formatTimer(sessionTimer)} (15 min - 8 hours)
              </p>
            </div>
          </div>
          
          <DialogFooter className="mt-6">
            <Button
              onClick={() => setShowTimerModal(false)}
              variant="outline"
              className="border-purple-600 text-purple-300 hover:bg-purple-600/20"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmStartSession}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              Start Session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move to Position Modal */}
      <Dialog open={showPositionModal} onOpenChange={setShowPositionModal}>
        <DialogContent className="bg-purple-900 border-purple-600 text-white max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="text-purple-100">Move Song to Position</DialogTitle>
            <DialogDescription className="text-purple-300">
              {selectedSong && `Move "${selectedSong.song_title}" to a new position in the queue.`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="position" className="text-purple-200">New Position</Label>
              <div className="flex items-center space-x-2 mt-2">
                <Input
                  id="position"
                  type="number"
                  min="1"
                  max={queue.length + 10}
                  value={newPosition}
                  onChange={(e) => setNewPosition(e.target.value)}
                  className="bg-purple-800 border-purple-600 text-white"
                  placeholder="Enter position number"
                />
                <span className="text-purple-300">of {queue.length}</span>
              </div>
              <p className="text-purple-400 text-sm mt-1">
                Current position: {selectedSong && queue.findIndex(s => s.id === selectedSong.id) + 1}
              </p>
              <p className="text-purple-400 text-sm">
                Tip: If you enter a position beyond the queue length, the song will be moved to the end.
              </p>
            </div>
          </div>
          
          <DialogFooter className="mt-6">
            <Button
              onClick={() => {
                setShowPositionModal(false);
                setSelectedSong(null);
                setNewPosition('');
              }}
              variant="outline"
              className="border-purple-600 text-purple-300 hover:bg-purple-600/20"
            >
              Cancel
            </Button>
            <Button
              onClick={() => moveToPosition(selectedSong, newPosition)}
              disabled={!newPosition || !selectedSong}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              Move Song
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Song Request Form Editor Modal */}
      <Dialog open={showFormEditor} onOpenChange={setShowFormEditor}>
        <DialogContent className="bg-purple-900 border-purple-600 text-white max-w-4xl mx-auto max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-purple-100 flex items-center">
              <Settings className="w-5 h-5 mr-2" />
              Song Request Form Editor
            </DialogTitle>
            <DialogDescription className="text-purple-300">
              Customize the fields that appear on your song request form. Required fields (Name, Song Title, Artist) cannot be removed.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Form Fields List */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-purple-200 font-semibold">Form Fields</h3>
                <Button
                  onClick={addFormField}
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  + Add Field
                </Button>
              </div>
              
              <div className="space-y-2">
                {formFields.map((field, index) => {
                  const isSkipQueueField = field.id === 'skip_queue';
                  const isReadOnly = isSkipQueueField; // skip_queue field is managed by paid request settings
                  
                  return (
                    <div
                      key={field.id}
                      className={`flex items-center space-x-3 p-3 rounded-lg border ${
                        isSkipQueueField 
                          ? 'bg-yellow-900/20 border-yellow-600/50' 
                          : 'bg-purple-800/30 border-purple-600/30'
                      }`}
                    >
                      {/* Field Order */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isSkipQueueField ? 'bg-yellow-600' : 'bg-purple-600'
                      }`}>
                        <span className="text-white text-sm font-bold">{field.order}</span>
                      </div>
                      
                      {/* Field Details */}
                      <div className="flex-1 space-y-2">
                        {isSkipQueueField && (
                          <div className="mb-2 p-2 bg-yellow-900/30 rounded text-yellow-200 text-xs">
                            <strong>Note:</strong> This field is automatically shown/hidden based on your "Paid Requests" toggle. 
                            It cannot be edited or removed here.
                          </div>
                        )}
                        <div className="flex items-center space-x-3">
                          <Input
                            value={field.label}
                            onChange={(e) => updateFormField(field.id, { label: e.target.value })}
                            className="bg-purple-800 border-purple-600 text-white flex-1"
                            placeholder="Field Label"
                            disabled={isReadOnly}
                            readOnly={isReadOnly}
                          />
                          <select
                            value={field.type}
                            onChange={(e) => updateFormField(field.id, { type: e.target.value })}
                            className="bg-purple-800 border border-purple-600 text-white px-3 py-2 rounded-md"
                            disabled={isReadOnly}
                          >
                            <option value="text">Text</option>
                            <option value="email">Email</option>
                            <option value="tel">Phone</option>
                            <option value="checkbox">Checkbox</option>
                            <option value="textarea">Text Area</option>
                          </select>
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`required-${field.id}`}
                              checked={field.required}
                              onChange={(e) => updateFormField(field.id, { required: e.target.checked })}
                              className="rounded border-purple-600 bg-purple-800 text-purple-600"
                              disabled={isReadOnly}
                            />
                            <label htmlFor={`required-${field.id}`} className="text-purple-300 text-sm">
                              Required
                            </label>
                          </div>
                        </div>
                        
                        {/* Field Preview */}
                        <div className="text-purple-400 text-sm">
                          Preview: {field.type === 'checkbox' ? '☐ ' : ''}{field.label || 'Untitled Field'}
                          {field.required && <span className="text-red-400 ml-1">*</span>}
                        </div>
                      </div>
                      
                      {/* Field Actions */}
                      <div className="flex items-center space-x-1">
                        <Button
                          onClick={() => moveFieldUp(field.id)}
                          size="sm"
                          variant="outline"
                          disabled={index === 0 || isReadOnly}
                          className="border-purple-600 text-purple-300 hover:bg-purple-600/20"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => moveFieldDown(field.id)}
                          size="sm"
                          variant="outline"
                          disabled={index === formFields.length - 1 || isReadOnly}
                          className="border-purple-600 text-purple-300 hover:bg-purple-600/20"
                        >
                          <ArrowDown className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => removeFormField(field.id)}
                          size="sm"
                          variant="destructive"
                          className="bg-red-600 hover:bg-red-700"
                          disabled={isReadOnly}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Mobile Form Preview */}
            <div className="space-y-3">
              <h3 className="text-purple-200 font-semibold">Mobile Preview (How your audience will see it)</h3>
              <div className="flex justify-center">
                <div className="w-80 h-[600px] bg-gray-900 rounded-3xl p-4 border-4 border-gray-700 shadow-2xl">
                  {/* Mobile Header */}
                  <div className="text-center mb-4">
                    <div className="w-16 h-1 bg-gray-600 rounded-full mx-auto mb-2"></div>
                    <h2 className="text-white font-bold text-lg">{profile?.artist_name || 'Karaoke Host'}</h2>
                    <p className="text-gray-400 text-sm">Song Request Form</p>
                  </div>
                  
                  {/* Mobile Form Content */}
                  <div className="space-y-4 max-h-[500px] overflow-y-auto">
                    {formFields.map((field) => (
                      <div key={field.id} className="space-y-2">
                        <label className="text-white text-sm font-medium block">
                          {field.label}
                          {field.required && <span className="text-red-400 ml-1">*</span>}
                        </label>
                        {field.type === 'textarea' ? (
                          <textarea
                            className="w-full bg-gray-800 border border-gray-600 text-white px-3 py-3 rounded-lg text-sm resize-none"
                            rows="3"
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                            disabled
                          />
                                                 ) : field.type === 'checkbox' ? (
                           <div className="flex items-start space-x-3 p-3 bg-gray-800 rounded-lg border border-gray-600">
                             <input
                               type="checkbox"
                               disabled
                               className="mt-1 rounded border-gray-600 bg-gray-800 text-purple-600"
                             />
                             <span className="text-gray-300 text-sm leading-relaxed">{field.label}</span>
                           </div>
                        ) : field.type === 'email' ? (
                          <input
                            type="email"
                            className="w-full bg-gray-800 border border-gray-600 text-white px-3 py-3 rounded-lg text-sm"
                            placeholder={`Enter your email address`}
                            disabled
                          />
                        ) : field.type === 'tel' ? (
                          <input
                            type="tel"
                            className="w-full bg-gray-800 border border-gray-600 text-white px-3 py-3 rounded-lg text-sm"
                            placeholder={`Enter your phone number`}
                            disabled
                          />
                        ) : (
                          <input
                            type="text"
                            className="w-full bg-gray-800 border border-gray-600 text-white px-3 py-3 rounded-lg text-sm"
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                            disabled
                          />
                        )}
                      </div>
                    ))}
                    
                    {/* Submit Button */}
                    <button
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-lg text-sm transition-colors"
                      disabled
                    >
                      Submit Song Request
                    </button>
                  </div>
                  
                  {/* Mobile Footer */}
                  <div className="text-center mt-4 pt-4 border-t border-gray-700">
                    <p className="text-gray-500 text-xs">Swipe to scroll • Tap to submit</p>
                  </div>
                </div>
              </div>
              
              {/* Mobile Preview Info */}
              <div className="text-center">
                <p className="text-purple-400 text-sm">
                  📱 This is how your song request form will appear on mobile devices
                </p>
                <p className="text-purple-500 text-xs mt-1">
                  Your audience will see this exact layout when requesting songs
                </p>
              </div>
            </div>
          </div>
          
          <DialogFooter className="mt-6 flex justify-between">
            <Button
              onClick={resetFormFieldsToDefault}
              variant="outline"
              className="border-orange-600 text-orange-300 hover:bg-orange-600/20"
            >
              Reset to Default
            </Button>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowFormEditor(false)}
                variant="outline"
                className="border-purple-600 text-purple-300 hover:bg-purple-600/20"
              >
                Cancel
              </Button>
              <Button
                onClick={saveFormFields}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                Save Form
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* Manual Entry Modal */}
      <Dialog open={showManualEntry} onOpenChange={setShowManualEntry}>
        <DialogContent className="bg-gradient-to-br from-purple-950 via-purple-900 to-indigo-950 border-purple-600 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-purple-100 flex items-center">
              <Music className="w-5 h-5 mr-2" />
              Manual Entry
            </DialogTitle>
            <DialogDescription className="text-purple-300">
              Add a song directly to the queue
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Song Title */}
            <div className="space-y-2">
              <Label htmlFor="manual-song-title" className="text-purple-200">
                Song Title <span className="text-red-400">*</span>
              </Label>
              <Input
                id="manual-song-title"
                value={manualEntryForm.song_title}
                onChange={(e) => setManualEntryForm({ ...manualEntryForm, song_title: e.target.value })}
                placeholder="Enter song title"
                className="bg-purple-800/50 border-purple-600 text-white placeholder:text-purple-400"
              />
            </div>

            {/* Artist Name */}
            <div className="space-y-2">
              <Label htmlFor="manual-artist-name" className="text-purple-200">
                Artist <span className="text-red-400">*</span>
              </Label>
              <Input
                id="manual-artist-name"
                value={manualEntryForm.artist_name}
                onChange={(e) => setManualEntryForm({ ...manualEntryForm, artist_name: e.target.value })}
                placeholder="Enter artist name"
                className="bg-purple-800/50 border-purple-600 text-white placeholder:text-purple-400"
              />
            </div>

            {/* Singer Name */}
            <div className="space-y-2">
              <Label htmlFor="manual-requester-name" className="text-purple-200">
                Singer Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="manual-requester-name"
                value={manualEntryForm.requester_name}
                onChange={(e) => setManualEntryForm({ ...manualEntryForm, requester_name: e.target.value })}
                placeholder="Enter singer name"
                className="bg-purple-800/50 border-purple-600 text-white placeholder:text-purple-400"
              />
            </div>

            {/* Note (Optional) */}
            <div className="space-y-2">
              <Label htmlFor="manual-note" className="text-purple-200">
                Note (Optional)
              </Label>
              <Textarea
                id="manual-note"
                value={manualEntryForm.note}
                onChange={(e) => setManualEntryForm({ ...manualEntryForm, note: e.target.value })}
                placeholder="Add any notes..."
                className="bg-purple-800/50 border-purple-600 text-white placeholder:text-purple-400"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={() => {
                setShowManualEntry(false);
                setManualEntryForm({
                  song_title: '',
                  artist_name: '',
                  requester_name: '',
                  note: ''
                });
              }}
              variant="outline"
              className="border-purple-600 text-purple-300 hover:bg-purple-600/20"
            >
              Cancel
            </Button>
            <Button
              onClick={submitManualEntry}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              Add to Queue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Tour Modal */}
      {showTour && (
        <KaraokeLDTour
          onComplete={handleTourComplete}
          onSkip={handleTourSkip}
        />
      )}

      {/* TEST Button - Bottom Right */}
      {isLive && (
        <Button
          onClick={createTestRequests}
          disabled={isCreatingTestRequests}
          size="sm"
          variant="outline"
          className="fixed bottom-4 right-4 text-xs px-2 py-1 h-auto text-gray-400 hover:text-gray-300 border-gray-600 hover:border-gray-500 bg-gray-900/50 backdrop-blur-sm z-50"
        >
          {isCreatingTestRequests ? 'Creating...' : 'TEST'}
        </Button>
      )}
    </>
  );
};

export default KaraokeLiveDashboardPage;
